/**
 * `/config/:slug` page — phase 41 sub-decision L.
 *
 * Per-studio L2 YAML editor with sectioned forms:
 *   1. Business basics
 *   2. Pricing — packages & memberships
 *   3. Session types (phase 41 followup)
 *   4. AI voice & behaviour
 *   5. Staff allowlist
 *   6. Marketing flows
 *   7. Booking system (advanced; collapsed by default)
 *   8. External integrations (advanced; hidden when booking_system.kind === 'layer-canonical')
 *
 * Full-replace patch semantics (sub-decision L1): the SPA sends the
 * ENTIRE L2 YAML config on every Save (not just the changed section).
 * Each section's local edit state mutates a SHARED copy of the loaded
 * config; clicking Save on any section writes the WHOLE thing through
 * `POST /api/studio-config/:slug` (which dispatches `edit_studio_config`).
 *
 * SIGUSR2 hot-reload (dev only) via `POST /api/studio-config/:slug/reload`.
 * Production deploys require a redeploy; the SPA UX surfaces a warning
 * banner when the reload endpoint returns 503 + `mode: 'production-redeploy-required'`.
 *
 * NO automated test coverage at phase 41 — per the post-phase-27 frontend-
 * test-infra carve-out. Mario verifies visually + via the smoke runbook.
 *
 * Per the brief's `## Out of scope` section:
 *   - Inline flow editing on the Marketing flows section. The section
 *     ONLY exposes per-studio toggles + a list of flow links pointing
 *     at phase 33a's `/flows/:flow_name/edit` editor.
 *   - Per-section RBAC.
 *   - Production-side automatic hot-reload without redeploy.
 *   - Editing the `studios` Postgres row (display_name / vertical / locale
 *     / timezone) — requires a separate `update_studio` canonical action.
 */

import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { DASHBOARD_PAGES } from "@layer/dashboard/page-registry";
import { api, ApiError } from "../lib/api";
import type {
  StudioConfigResponse,
  StudioConfigSectionMetadata,
} from "../lib/api";
import { dispatchAction } from "../lib/dispatch";
import { ActionModalButton } from "../components/ActionModalButton";
import { useWhoami } from "../components/DashboardLayout";
import {
  invalidate,
  peekEntry,
  setEntry,
  useCachedResource,
} from "../lib/resource-cache";
import { invalidateStudioConfig } from "../lib/resource-cache-invalidation";
import {
  applyConnectorCapability,
  applyConnectorState,
  applyWebchatEnabled,
  type ConnectorAiState,
} from "../lib/studio-config-patch";
import { ConnectorAiRow } from "../components/ConnectorAiRow";
import {
  ConnectorChatRow,
  type ConnectorChatState,
} from "../components/ConnectorChatRow";
import {
  buildFlagToggleList,
  buildGridToggleList,
  hasUnsavedTextEdits,
  mergeFlags,
  withGridCell,
  type StaffAllowlistFlagField,
} from "../lib/staff-allowlist-edit";
import { ScheduledJobsSection } from "../components/ScheduledJobsSection";
import {
  KNOWN_SECRET_KINDS,
  KNOWN_SECRET_KIND_LABELS,
} from "@layer/canonical-data/secret-kinds";
import {
  CONNECTOR_DESCRIPTORS,
  isConnectorEnabled,
  resolveConnectorAutonomy,
  resolveConnectorCapability,
  type AiAutonomyMode,
  type ConnectorCapability,
  type ConnectorKey,
  type ConnectorSettings,
  type StripeOutboundEnvironment,
} from "@layer/canonical-data/connectors";

// ---------------------------------------------------------------------------
// Local types (mirror the L2 YAML shapes from the server side; defined
// here to avoid a runtime dep on the canonical-data package from the
// browser bundle).
// ---------------------------------------------------------------------------

interface PackageOffering {
  slug: string;
  name: string;
  sessions_total: number;
  price_minor: number;
  currency: string;
  validity_days?: number;
  /** Session-type slug ∈ session_types[].slug (studio / home). */
  session_type?: string;
}

interface MembershipOffering {
  slug: string;
  name: string;
  billing_period: "monthly" | "quarterly" | "annual";
  price_minor: number;
  currency: string;
  sessions_per_cycle?: number | "unlimited";
  resets_per_cycle?: boolean;
  /** Session-type slug ∈ session_types[].slug (studio / home). */
  session_type?: string;
}

interface StaffAllowlistEntry {
  phone: string;
  staff_id: string;
  display_name: string;
  /**
   * Phase 64 admin tier (migration 042). `true` → this phone's chat agent
   * bypasses the tool allowlist (full human:staff catalog); absent/false →
   * normal staff (allowlist-filtered like a member).
   */
  admin?: boolean;
  /**
   * Migration 160 — "Don't send me any messages". `true` → `notify_staff`
   * never resolves this person as a recipient, on any channel, for any
   * audience. Orthogonal to `admin`: capability vs contactability.
   */
  alerts_muted?: boolean;
  /**
   * 2026-08-12 — the two columns the editor never rendered.
   *
   * `email` is where `notify_staff` sends this person's alerts;
   * `telegram_chat_id` is what resolves an INBOUND Telegram message from them
   * to this staff row. Both were unrenderable AND unwriteable until now: the
   * writer carried them across the full-replace keyed on PHONE, so nothing
   * could set them and CHANGING SOMEONE'S NUMBER silently dropped both.
   * Rendering them is what ends the carry-over's blind spot — the form now
   * sends the real value, so the phone is no longer load-bearing.
   *
   * `""` in the box means CLEAR (the server maps it to null); `null` from the
   * server means "not set".
   */
  email?: string | null;
  telegram_chat_id?: string | null;
  /**
   * Migration 177 — the address this person SIGNS IN to the dashboard with.
   *
   * Not a duplicate of `email`: that one is where `notify_staff` WRITES to
   * them, and on production the two are genuinely different addresses for the
   * same human. The dashboard resolves a browser session to a staff row by
   * the `app_metadata.staff_id` claim first, then this column, then `email` —
   * and once per-staff page access lands, a row that resolves to nothing means
   * a person who can see nothing.
   *
   * `""` in the box means CLEAR; `null` from the server means "not set", which
   * the card renders as an explicit unbound warning rather than a blank.
   */
  login_email?: string | null;
  /**
   * Migration 178 — which dashboard pages this person can open.
   *
   * `null` / absent = UNRESTRICTED, which is every row's default and what the
   * roster shows as "all". An ARRAY restricts to exactly those page ids (plus
   * the always-allowed Home). The EMPTY array is a real value meaning
   * "restricted to nothing" — never a synonym for `null`, or unticking every
   * page would silently grant every page.
   *
   * Edited in the per-person Settings panel and saved with the button, NOT
   * instantly: it is a list, and half a list committed mid-edit is a worse
   * accident than a stale checkbox.
   */
  visible_pages?: readonly string[] | null;
  /**
   * Migration 173 — the voter grant (WHO may vote on staff decisions; the
   * grid's `decisions` row is WHERE ballots reach them). Instant-save
   * checkbox on the same `toggleFlag` path as `admin` / `alerts_muted`;
   * defaults FALSE (an explicit grant).
   */
  voter?: boolean;
  /**
   * Migration 192 — the per-person notification grid
   * `{ "<type>": ["whatsapp"|"telegram"|"email", ...] }`, replacing the 17
   * `notify_*` boolean rails. Rendered as three capability-filtered channel
   * sections in the Settings panel; every tick instant-saves via
   * `buildGridToggleList`. An absent TYPE is OFF (no empty arrays are ever
   * stored); an absent GRID means the row predates the client's knowledge
   * and the POST omits the key so the server keeps the stored value.
   */
  notification_grid?: Readonly<Record<string, ReadonlyArray<string>>>;
}

interface SessionType {
  slug: string;
  label: string;
  duration_minutes: number;
  klaviyo_label?: string;
}

/** 2026-07-05 — per-audience visibility flags for one catalog entry. */
interface CatalogEntryVisibility {
  members?: boolean;
  staff?: boolean;
}

/**
 * 2026-08-04 — one set of pay terms (the `payroll.defaults` block or one
 * trainer's override row). Mirrors `PayrollTrainerCompensation` in
 * `packages/canonical-data/src/l2-yaml.ts`.
 */
interface PayrollCompShape {
  pay_model: "hourly" | "revenue_share";
  hourly_rate_minor: number | null;
  revenue_share_pct: number | null;
  pr_session_rate_minor: number | null;
}

/** The L2 `payroll:` block (trainer-ops lane B). Override-covered → live Save. */
interface PayrollBlockShape {
  effective_from: string;
  pay_no_shows: boolean;
  bonus: { new_signup_pct: number; renewal_pct: number };
  defaults?: PayrollCompShape;
  trainers?: Record<string, PayrollCompShape>;
}

interface StudioL2ConfigShape {
  cancellation_deadline_hours: number;
  packages: PackageOffering[];
  memberships: MembershipOffering[];
  session_types: SessionType[];
  /**
   * 2026-08-04 — trainer payroll config (pay models, rates, bonus pcts).
   * Surfaced by the Trainer payroll section; consumed by the payroll engine
   * via each run's frozen config snapshot.
   */
  payroll?: PayrollBlockShape;
  /**
   * 2026-07-05 — slug-keyed visibility over the packages + memberships
   * catalogs. UNLIKE the pricing catalogs this IS a live studio_config
   * override field (survives the Save catalog-strip), so these toggles take
   * effect with no redeploy. Absent slug / flag ⇒ visible to that audience.
   */
  catalog_visibility?: {
    packages?: Record<string, CatalogEntryVisibility>;
    memberships?: Record<string, CatalogEntryVisibility>;
  };
  /**
   * 2026-07-27 — per-connector activate/deactivate. Live studio_config
   * override field: a Save pauses a vendor with no redeploy. Absent key /
   * flag ⇒ that connector is ENABLED.
   */
  connectors?: Partial<Record<ConnectorKey, ConnectorSettings>>;
  booking_system: {
    kind: string;
    config: Record<string, unknown>;
    /**
     * 2026-09-02 — how the studio schedules availability. `dated`: every rota
     * is uploaded for explicit dates, undated windows are rejected, the
     * nightly roller skips the studio. `ongoing` (or absent): permanent weekly
     * windows are admitted and the roller extends them. Live on Save.
     */
    availability_model?: "dated" | "ongoing";
    /**
     * clavis-founding-call (2026-09-14) — session types a member may book
     * WITHOUT a credit (the member-api `book-complimentary` endpoint grants the
     * rule's zero-price package as a comp, then books). Cross-checked against
     * the catalogs at load time, and the catalogs are deploy-time-only, so the
     * rules are shown here READ-ONLY and edited in the studio's git YAML.
     */
    complimentary?: ReadonlyArray<{
      session_type: string;
      package_slug: string;
      limit_per_member: number;
      min_notice_minutes: number;
    }>;
  };
  /**
   * 2026-08-07 — the Gmail block, now EDITED here rather than merely surviving
   * here. Before the Mailboxes section it was reachable only because this shape
   * ends in an index signature and the page POSTs the whole object, so a Save
   * round-tripped `gmail` by accident; typing it makes that deliberate.
   *
   * `impersonate_user` + `self_addresses` are declared but NOT edited by the
   * section — it mutates `mailboxes` only, and the untouched siblings ride the
   * same full-object POST. The block has a CLOSED key set on the backend
   * (`GMAIL_KEYS`) which throws on anything unknown, and a `/config` Save is
   * live with no deploy, so nothing may invent a field here.
   */
  gmail?: {
    impersonate_user?: string;
    mailboxes?: GmailMailboxShape[];
    self_addresses?: string[];
  };
  resend?: { from_address?: string };
  /** Phase 41 followup — Stripe Checkout default URLs applied by `charge_member`. */
  stripe?: {
    checkout_success_url?: string;
    checkout_cancel_url?: string;
    /**
     * 2026-07-27 — which Stripe ACCOUNT outbound money actions run against.
     * Absent ⇒ 'test' (the sandbox), which is what runs today.
     */
    outbound_environment?: StripeOutboundEnvironment;
  };
  studio?: {
    brand_voice?: string;
    whatsapp_number?: string;
    /** `null` = deliberately OFF (beats the git-YAML baseline); absent = inherit. */
    ops_alert_whatsapp?: string | null;
    signup_channel?: "whatsapp" | "email" | "sms";
    /**
     * host-scoped-staff-login (2026-09-14) — the studio's own dashboard
     * origin + any further hosts that resolve back to it. No editor field
     * yet; declared so an existing override round-trips losslessly through
     * the full-replace save instead of being dropped.
     */
    dashboard_url?: string;
    dashboard_url_aliases?: string[];
    branding?: {
      logo_url?: string;
      primary_color?: string;
      heading_font?: string;
      heading_font_size?: string;
      body_font?: string;
      body_font_size?: string;
      /** Brand-assets library — the public citation host (bare https host). */
      assets_base_url?: string;
    };
    website?: string;
    address?: string;
    opening_hours?: string;
    social_media?: Record<string, string>;
    /**
     * Onboarding/test routing for the morning brief (T1) + post-session
     * capture (T3), relative to the studio's staff_allowlist `admin = true`
     * number(s). Those TWO flows only — rota asks, payroll statements, the
     * task digest and polls do not honour it.
     *
     *   `off`      — each trainer's own phone (the default; the key is DROPPED
     *                rather than written when the operator picks this).
     *   `redirect` — the admin number(s) INSTEAD of the trainer, including
     *                trainers with no phone on file (preview-everyone).
     *   `copy`     — the trainer AND the admin number(s); a trainer with no
     *                phone is skipped entirely, since there is no send to copy.
     *
     * Replaced the `redirect_trainer_comms_to_admins` boolean 2026-08-19.
     * notify_staff manager/admin alerts already go to the admins and are
     * unchanged.
     */
    trainer_comms_admin_mode?: "off" | "redirect" | "copy";
  };
  chat_translator?: {
    max_llm_calls_per_member_per_24h?: number;
    onboarding_guidance?: string;
    /** Legacy single debounce window (seconds). Superseded by the staff/member pair. */
    debounce_seconds?: number;
    /** 2026-06-04 — per-actor debounce windows (seconds). Staff 0 (immediate); member 5. Range 0..120. */
    debounce_seconds_staff?: number;
    debounce_seconds_member?: number;
    /** Phase 41 followup — legacy SHARED tool allowlist (member + non-admin staff fallback). */
    tool_allowlist?: ReadonlyArray<string>;
    /**
     * Phase 66d — PER-TIER tool allowlists. Each ABSENT ⇒ that tier inherits
     * its fallback (member/staff → legacy `tool_allowlist`; admin → full
     * staff catalog). PRESENT ⇒ that tier's catalog is restricted to the
     * listed actions (admin list, when present, ends the historical bypass).
     */
    tool_allowlist_member?: ReadonlyArray<string>;
    tool_allowlist_staff?: ReadonlyArray<string>;
    tool_allowlist_admin?: ReadonlyArray<string>;
    /** Phase 66c — live per-studio override of the system-prompt skeleton + role blocks. */
    system_prompt?: string;
    customer_instructions?: string;
    staff_instructions?: string;
    /**
     * C1 — per-tier model override (Anthropic model id). Each ABSENT ⇒ that
     * tier uses the client default (Sonnet 4.6). `model_staff` also covers
     * the admin sub-tier. Resolved ONCE per conversation (stable across
     * turns to preserve the prompt cache); live via the studio_config
     * override, so a change applies to the next new conversation.
     */
    model_member?: string;
    model_staff?: string;
  };
  // NO `staff` key. The allowlist rows live in the `staff_allowlist` table and
  // the card below saves them to `/api/staff-allowlist/:slug`; the `staff`
  // CONFIG block was retired on 2026-08-11 (it was a second copy this page
  // round-tripped on every save without ever rendering it). The validator now
  // rejects the key, so a save that re-introduced it would fail loudly.
  marketing_flows?: {
    max_active_flows_per_member?: number;
    opt_out_url_template?: string;
  };
  /**
   * Per-studio control of the `audit_error_alerter` cron (the every-15-min
   * audit-log error → admin WhatsApp digest). `enabled` ABSENT ⇒ true; only
   * an explicit `false` pauses the studio's scan. `excluded_error_codes` is
   * ADDITIVE to the saga's two hardcoded loop-safety exclusions and carries
   * bare codes WITHOUT the `error:` prefix.
   *
   * 2026-08-25 — the dashboard EDITOR for this block was removed; "who
   * receives error alerts" is per-person (migration 185's opt-in rail, the
   * grid's `error` row since migration 192). This TYPE is deliberately KEPT
   * (not deleted with the editor) so any existing override set via git YAML
   * round-trips losslessly through the config editor's full-replace save
   * instead of being silently dropped. Still read by the backend alerter;
   * settable only via git YAML now.
   */
  error_alerts?: {
    enabled?: boolean;
    excluded_error_codes?: ReadonlyArray<string>;
  };
  klaviyo?: {
    session_type_to_duration_minutes?: Record<string, number>;
    instructor_name_to_trainer_id?: Record<string, string>;
  };
  /**
   * Phase 41a — knowledge base. ARRAY of `{topic, content}` markdown
   * sections answering factual customer questions. Read by
   * `call_knowledge_base`; surfaced as a tool to the chat-translator
   * (phase 31c). Optional on the SPA shape (defaults `[]` on the
   * backend); section #8 of the editor manages this field.
   */
  knowledge_base?: KnowledgeBaseEntry[];
  /**
   * Phase 57 + 61 — per-person memory studio config. Preambles + recycle
   * budgets (57) and the three LLM generation prompts + informational cron
   * schedules (61). Surfaced by the Memory section. The `*_cron` fields are
   * display-only (the live cron is a global Inngest function set at deploy).
   */
  memory?: {
    member_preamble?: string;
    staff_preamble?: string;
    rolling_retention_days?: number;
    longterm_token_budget?: number;
    daily_summary_member_prompt?: string;
    daily_summary_staff_prompt?: string;
    recycler_prompt?: string;
    daily_writer_cron?: string;
    recycler_cron?: string;
  };
  [k: string]: unknown;
}

interface KnowledgeBaseEntry {
  topic: string;
  content: string;
}

/**
 * One `gmail.mailboxes[]` entry. Mirrors the backend shape exactly
 * (`packages/canonical-data/src/l2-yaml.ts`): `scan` + `send_as` are REQUIRED
 * booleans with deliberately no default (neither side is safe to assume),
 * while `member_mail` is OPTIONAL and defaults to false — absent ≡ false, and
 * the validator DROPS an explicit `false` from its output, so the editor must
 * drop it too or every Save reports a phantom change.
 */
interface GmailMailboxShape {
  address: string;
  label: string;
  scan: boolean;
  send_as: boolean;
  member_mail?: boolean;
}

// ---------------------------------------------------------------------------
// UUID v7 — shared with FlowEdit + Dispatch.
// ---------------------------------------------------------------------------

function uuidv7(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  const t = BigInt(Date.now());
  bytes[0] = Number((t >> 40n) & 0xffn);
  bytes[1] = Number((t >> 32n) & 0xffn);
  bytes[2] = Number((t >> 24n) & 0xffn);
  bytes[3] = Number((t >> 16n) & 0xffn);
  bytes[4] = Number((t >> 8n) & 0xffn);
  bytes[5] = Number(t & 0xffn);
  bytes[6] = (bytes[6]! & 0x0f) | 0x70;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
}

// ---------------------------------------------------------------------------
// Section renderers.
//
// Each section accepts the full config + a mutator. Save lives at the
// PAGE level (sub-decision L1 full-replace semantics).
// ---------------------------------------------------------------------------

interface SectionProps {
  config: StudioL2ConfigShape;
  setConfig: (c: StudioL2ConfigShape) => void;
}

function setOrDrop<T extends Record<string, unknown>>(
  obj: T,
  key: keyof T,
  value: string,
): T {
  // Helper for exactOptionalPropertyTypes: dropping undefined values
  // instead of assigning `undefined` (which the strict optional types
  // refuse to accept).
  const next = { ...obj };
  if (value === "") {
    delete next[key];
  } else {
    (next as Record<string, unknown>)[key as string] = value;
  }
  return next;
}

function setOrDropAny<T extends Record<string, unknown>>(
  obj: T,
  key: keyof T,
  value: unknown,
): T {
  const next = { ...obj };
  if (
    value === undefined ||
    value === null ||
    (typeof value === "string" && value === "")
  ) {
    delete next[key];
  } else {
    (next as Record<string, unknown>)[key as string] = value;
  }
  return next;
}

/**
 * Studio timezone section — edits `studios.timezone` (the Postgres row, NOT
 * the L2 YAML), so it has its OWN Save that dispatches the
 * `set_studio_timezone` canonical action instead of riding the page-level
 * YAML full-replace save. Every time-aware surface — chat slot times,
 * availability labels, cron local-hour gates, and this dashboard's own
 * display pinning (`studio-tz`) — follows the value, so a successful save
 * forces a full reload to re-pin the open session.
 */
function StudioTimezoneSection(): JSX.Element {
  const whoami = useWhoami();
  const currentTz = whoami.data?.studio.timezone ?? "";
  const [tz, setTz] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const value = tz ?? currentTz;
  const dirty = tz !== null && tz !== currentTz;

  const save = useCallback(async () => {
    const candidate = (tz ?? "").trim();
    if (candidate === "") return;
    // Client pre-check with the exact semantics the action enforces (ICU) —
    // catches typos before a round-trip; the chokepoint stays authoritative.
    try {
      new Intl.DateTimeFormat("en-US", { timeZone: candidate });
    } catch {
      setError(
        `"${candidate}" is not a valid IANA timezone (e.g. Europe/London, Europe/Berlin).`,
      );
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const r = await dispatchAction<
        { timezone: string },
        { timezone?: string }
      >("set_studio_timezone", { timezone: candidate });
      if (r.ok) {
        // Re-pin the whole session: whoami, the studio-tz module snapshot,
        // and every page's cached formatters reload with the new zone.
        window.location.reload();
        return;
      }
      setSaving(false);
      setError(`Save failed: ${r.message || r.code}`);
    } catch (e) {
      setSaving(false);
      setError((e as Error).message);
    }
  }, [tz]);

  return (
    <fieldset>
      <legend>Studio timezone</legend>
      <p className="subtle">
        The single timezone every surface follows — WhatsApp slot times,
        booking availability, scheduled sends, and all times shown on this
        dashboard. Stored on the studio record (not the YAML); saving reloads
        the page so open views re-pin.
      </p>
      <div className="form-row">
        <label>
          IANA zone
          <input
            type="text"
            list="studio-tz-options"
            value={value}
            placeholder="Europe/London"
            onChange={(e) => setTz(e.target.value)}
          />
        </label>
        <datalist id="studio-tz-options">
          <option value="Europe/London" />
          <option value="Europe/Berlin" />
          <option value="Europe/Paris" />
          <option value="Europe/Madrid" />
          <option value="America/New_York" />
          <option value="Asia/Dubai" />
        </datalist>
        <button
          type="button"
          disabled={saving || !dirty || value.trim() === ""}
          onClick={() => void save()}
        >
          {saving ? "Saving…" : "Save timezone"}
        </button>
      </div>
      {error !== null && <div className="tag tag-danger">{error}</div>}
    </fieldset>
  );
}

// Phase 41 followup — Business basics section DELETED. Its single
// remaining field (`cancellation_deadline_hours`) moved into the
// Pricing section under memberships. Contact info + Branding got
// their own dedicated sections.
//
// Phase 41 followup — NEW Contact information section.
function ContactInformationSection({
  config,
  setConfig,
}: SectionProps): JSX.Element {
  const studio = config.studio ?? {};
  const socialMedia = studio.social_media ?? {};
  // Locked-display platforms (others remain editable via YAML but the
  // form renders these four prominently as the common ones).
  const PLATFORMS: ReadonlyArray<{ key: string; label: string; placeholder: string }> = [
    { key: "instagram", label: "Instagram", placeholder: "https://www.instagram.com/yourhandle/" },
    { key: "facebook", label: "Facebook", placeholder: "https://www.facebook.com/yourhandle" },
    { key: "tiktok", label: "TikTok", placeholder: "https://www.tiktok.com/@yourhandle" },
    { key: "youtube", label: "YouTube", placeholder: "https://www.youtube.com/@yourhandle" },
  ];

  const setSocialMedia = (key: string, value: string) => {
    const next = { ...socialMedia };
    if (value.trim() === "") {
      delete next[key];
    } else {
      next[key] = value;
    }
    setConfig({
      ...config,
      studio: setOrDropAny(
        studio,
        "social_media",
        Object.keys(next).length === 0 ? undefined : next,
      ),
    });
  };

  return (
    <fieldset>
      <legend>Contact information</legend>
      <div className="form-row">
        <label>WhatsApp number (E.164, e.g. +447700900123)</label>
        <input
          type="text"
          value={studio.whatsapp_number ?? ""}
          onChange={(e) =>
            setConfig({
              ...config,
              studio: setOrDrop(studio, "whatsapp_number", e.target.value),
            })
          }
          placeholder="+447700900123"
        />
      </div>
      <div className="form-row">
        <label>Ops alert WhatsApp (E.164, e.g. +447700900123)</label>
        <input
          type="text"
          value={studio.ops_alert_whatsapp ?? ""}
          onChange={(e) =>
            setConfig({
              ...config,
              // Empty ⇒ explicit `null`, NOT a dropped key — so this deliberately
              // does NOT use setOrDrop/setOrDropAny, both of which delete the key
              // for "" AND for null. Dropping it means "inherit", and the loader
              // merges the override OVER the git-YAML baseline, so on any studio
              // whose YAML sets this field (E-Pulsive's does) clearing the box
              // saved fine and changed nothing. `null` is the one value that
              // beats the baseline, and it round-trips: the loader returns it, so
              // the next unrelated Save re-sends it rather than reverting to
              // inherit.
              studio: {
                ...studio,
                ops_alert_whatsapp:
                  e.target.value === "" ? null : e.target.value,
              },
            })
          }
          placeholder="+447700900123"
        />
        <p className="hint">
          Where infrastructure alarms go — undeliverable WhatsApp messages, and
          the Peter / efficiency watchdogs. Your own line, not a staff or member
          address, and deliberately separate from the staff allowlist below so a
          bad allowlist entry can&apos;t misdirect the alerts that report it.{" "}
          <strong>Leave blank to switch every ops alert off for this studio.</strong>
        </p>
      </div>
      <div className="form-row">
        <label>Resend from-address (RFC 5322 email)</label>
        <input
          type="email"
          value={config.resend?.from_address ?? ""}
          onChange={(e) =>
            setConfig({
              ...config,
              resend: setOrDrop(
                config.resend ?? {},
                "from_address",
                e.target.value,
              ),
            })
          }
          placeholder="hello@example.com"
        />
      </div>
      <div className="form-row">
        <label>Website (https://…)</label>
        <input
          type="url"
          value={studio.website ?? ""}
          onChange={(e) =>
            setConfig({
              ...config,
              studio: setOrDrop(studio, "website", e.target.value),
            })
          }
          placeholder="https://e-pulsive.co.uk"
        />
      </div>
      <div className="form-row">
        <label>Address</label>
        <textarea
          rows={3}
          value={studio.address ?? ""}
          onChange={(e) =>
            setConfig({
              ...config,
              studio: setOrDrop(studio, "address", e.target.value),
            })
          }
          placeholder={"Studio name\nStreet,\nCity POSTCODE"}
        />
      </div>
      <div className="form-row">
        <label>Opening hours</label>
        <textarea
          rows={3}
          value={studio.opening_hours ?? ""}
          onChange={(e) =>
            setConfig({
              ...config,
              studio: setOrDrop(studio, "opening_hours", e.target.value),
            })
          }
          placeholder={"Mon-Fri 7am – 9pm\nSat 8am – 4pm\nSun 9am – 4pm"}
        />
      </div>
      <p className="hint" style={{ marginTop: 12, marginBottom: 4 }}>
        Social media (handles or full URLs)
      </p>
      {PLATFORMS.map((p) => (
        <div className="form-row" key={p.key}>
          <label>{p.label}</label>
          <input
            type="text"
            value={socialMedia[p.key] ?? ""}
            onChange={(e) => setSocialMedia(p.key, e.target.value)}
            placeholder={p.placeholder}
          />
        </div>
      ))}
    </fieldset>
  );
}

// Phase 41 followup — NEW Branding section. Logo + primary color +
// typography (heading + body fonts + sizes). Font names + sizes pass
// through to the future consent UI as CSS values; minimal validation
// at the schema layer.
function BrandingSection({ config, setConfig }: SectionProps): JSX.Element {
  const studio = config.studio ?? {};
  const branding = studio.branding ?? {};

  const updateBranding = (key: string, value: string) =>
    setConfig({
      ...config,
      studio: {
        ...studio,
        branding: setOrDrop(branding, key as keyof typeof branding, value),
      },
    });

  return (
    <fieldset>
      <legend>Branding</legend>
      <div className="form-row">
        <label>Logo URL (https://…)</label>
        <input
          type="url"
          value={branding.logo_url ?? ""}
          onChange={(e) => updateBranding("logo_url", e.target.value)}
          placeholder="https://e-pulsive.co.uk/images/logo.png"
        />
      </div>
      <div className="form-row">
        <label>Primary color (#rrggbb)</label>
        <input
          type="text"
          value={branding.primary_color ?? ""}
          onChange={(e) => updateBranding("primary_color", e.target.value)}
          placeholder="#1B33C0"
        />
      </div>
      <p className="hint" style={{ marginTop: 12, marginBottom: 4 }}>
        Typography
      </p>
      <div className="form-row">
        <label>Heading font</label>
        <input
          type="text"
          value={branding.heading_font ?? ""}
          onChange={(e) => updateBranding("heading_font", e.target.value)}
          placeholder="Inter, sans-serif"
        />
      </div>
      <div className="form-row">
        <label>Heading font size</label>
        <input
          type="text"
          value={branding.heading_font_size ?? ""}
          onChange={(e) => updateBranding("heading_font_size", e.target.value)}
          placeholder="32px"
        />
      </div>
      <div className="form-row">
        <label>Body font</label>
        <input
          type="text"
          value={branding.body_font ?? ""}
          onChange={(e) => updateBranding("body_font", e.target.value)}
          placeholder="Inter, sans-serif"
        />
      </div>
      <div className="form-row">
        <label>Body font size</label>
        <input
          type="text"
          value={branding.body_font_size ?? ""}
          onChange={(e) => updateBranding("body_font_size", e.target.value)}
          placeholder="16px"
        />
      </div>
      <p className="hint" style={{ marginTop: 12, marginBottom: 4 }}>
        Brand assets
      </p>
      <div className="form-row">
        <label>Assets host (https://…, no path or trailing slash)</label>
        <input
          type="url"
          value={branding.assets_base_url ?? ""}
          onChange={(e) => updateBranding("assets_base_url", e.target.value)}
          placeholder="https://assets.e-pulsive.co.uk"
        />
        <span className="hint">
          The public host the Assets page cites uploads from; the site behind it
          must proxy unknown names to the Supabase bucket. Leave empty to cite the
          raw Supabase URL.
        </span>
      </div>
    </fieldset>
  );
}

function PricingSection({ config, setConfig }: SectionProps): JSX.Element {
  const setPackages = (packages: PackageOffering[]) =>
    setConfig({ ...config, packages });
  const setMemberships = (memberships: MembershipOffering[]) =>
    setConfig({ ...config, memberships });
  const sessionTypeOptions = config.session_types ?? [];

  // 2026-07-05 — per-audience catalog visibility. Default-visible: a slug with
  // no entry (or no per-audience flag) is shown to that audience. Only
  // NON-default (hidden) entries are stored, so the override map stays minimal
  // and un-hiding a slug removes its entry entirely.
  const isVisible = (
    kind: "packages" | "memberships",
    slug: string,
    audience: "members" | "staff",
  ): boolean => config.catalog_visibility?.[kind]?.[slug]?.[audience] !== false;

  const setVisibility = (
    kind: "packages" | "memberships",
    slug: string,
    audience: "members" | "staff",
    visible: boolean,
  ): void => {
    const cv = config.catalog_visibility ?? {};
    const kindMap: Record<string, CatalogEntryVisibility> = {
      ...(cv[kind] ?? {}),
    };
    const entry: CatalogEntryVisibility = { ...(kindMap[slug] ?? {}) };
    if (visible) {
      // Default state ⇒ drop the flag (and the slug entry once empty).
      delete entry[audience];
    } else {
      entry[audience] = false;
    }
    if (entry.members === undefined && entry.staff === undefined) {
      delete kindMap[slug];
    } else {
      kindMap[slug] = entry;
    }
    setConfig({ ...config, catalog_visibility: { ...cv, [kind]: kindMap } });
  };

  return (
    <fieldset>
      <legend>Pricing — packages & memberships</legend>
      <p className="hint">
        The <strong>members</strong> / <strong>staff&amp;admin</strong>{" "}
        checkboxes control which packages &amp; memberships the WhatsApp chat
        agent can see and offer to each audience. Untick <strong>members</strong>{" "}
        to hide an offering from the member-facing agent (staff/admin still see
        it). These toggles are a live override — they take effect on Save with
        NO redeploy (unlike the pricing fields, which are deploy-time only).
      </p>
      <h3>Packages</h3>
      <table className="config-table">
        <thead>
          <tr>
            <th>slug</th>
            <th>name</th>
            <th>sessions_total</th>
            <th>price_minor</th>
            <th>currency</th>
            <th>validity_days</th>
            <th>session_type</th>
            <th title="Visible to the member-facing WhatsApp agent">members</th>
            <th title="Visible to the staff/admin chat agent">staff&amp;admin</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {config.packages.map((p, idx) => (
            <tr key={idx}>
              <td>
                <input
                  type="text"
                  value={p.slug}
                  onChange={(e) =>
                    setPackages(
                      config.packages.map((x, i) =>
                        i === idx ? { ...x, slug: e.target.value } : x,
                      ),
                    )
                  }
                />
              </td>
              <td>
                <input
                  type="text"
                  value={p.name}
                  onChange={(e) =>
                    setPackages(
                      config.packages.map((x, i) =>
                        i === idx ? { ...x, name: e.target.value } : x,
                      ),
                    )
                  }
                />
              </td>
              <td>
                <input
                  type="number"
                  value={p.sessions_total}
                  onChange={(e) =>
                    setPackages(
                      config.packages.map((x, i) =>
                        i === idx
                          ? { ...x, sessions_total: Number(e.target.value) || 0 }
                          : x,
                      ),
                    )
                  }
                />
              </td>
              <td>
                <input
                  type="number"
                  value={p.price_minor}
                  onChange={(e) =>
                    setPackages(
                      config.packages.map((x, i) =>
                        i === idx
                          ? { ...x, price_minor: Number(e.target.value) || 0 }
                          : x,
                      ),
                    )
                  }
                />
              </td>
              <td>
                <input
                  type="text"
                  value={p.currency}
                  onChange={(e) =>
                    setPackages(
                      config.packages.map((x, i) =>
                        i === idx ? { ...x, currency: e.target.value } : x,
                      ),
                    )
                  }
                />
              </td>
              <td>
                <input
                  type="number"
                  value={p.validity_days ?? ""}
                  onChange={(e) =>
                    setPackages(
                      config.packages.map((x, i) =>
                        i === idx
                          ? (setOrDropAny(
                              x as unknown as Record<string, unknown>,
                              "validity_days",
                              e.target.value
                                ? Number(e.target.value)
                                : undefined,
                            ) as unknown as PackageOffering)
                          : x,
                      ),
                    )
                  }
                />
              </td>
              <td>
                <select
                  value={p.session_type ?? ""}
                  onChange={(e) =>
                    setPackages(
                      config.packages.map((x, i) =>
                        i === idx
                          ? (setOrDropAny(
                              x as unknown as Record<string, unknown>,
                              "session_type",
                              e.target.value ? e.target.value : undefined,
                            ) as unknown as PackageOffering)
                          : x,
                      ),
                    )
                  }
                >
                  <option value="">— none —</option>
                  {sessionTypeOptions.map((st) => (
                    <option key={st.slug} value={st.slug}>
                      {st.label} ({st.slug})
                    </option>
                  ))}
                </select>
              </td>
              <td style={{ textAlign: "center" }}>
                <input
                  type="checkbox"
                  aria-label={`Package ${p.slug} visible to members`}
                  checked={isVisible("packages", p.slug, "members")}
                  onChange={(e) =>
                    setVisibility(
                      "packages",
                      p.slug,
                      "members",
                      e.target.checked,
                    )
                  }
                />
              </td>
              <td style={{ textAlign: "center" }}>
                <input
                  type="checkbox"
                  aria-label={`Package ${p.slug} visible to staff/admin`}
                  checked={isVisible("packages", p.slug, "staff")}
                  onChange={(e) =>
                    setVisibility("packages", p.slug, "staff", e.target.checked)
                  }
                />
              </td>
              <td>
                <button
                  type="button"
                  onClick={() =>
                    setPackages(config.packages.filter((_, i) => i !== idx))
                  }
                >
                  Remove
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button
        type="button"
        onClick={() =>
          setPackages([
            ...config.packages,
            {
              slug: "new-package",
              name: "New package",
              sessions_total: 1,
              price_minor: 0,
              currency: "GBP",
            },
          ])
        }
      >
        + Add package
      </button>

      <h3>Memberships</h3>
      <table className="config-table">
        <thead>
          <tr>
            <th>slug</th>
            <th>name</th>
            <th>billing_period</th>
            <th>price_minor</th>
            <th>currency</th>
            <th>sessions_per_cycle</th>
            <th>session_type</th>
            <th title="Visible to the member-facing WhatsApp agent">members</th>
            <th title="Visible to the staff/admin chat agent">staff&amp;admin</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {config.memberships.map((m, idx) => (
            <tr key={idx}>
              <td>
                <input
                  type="text"
                  value={m.slug}
                  onChange={(e) =>
                    setMemberships(
                      config.memberships.map((x, i) =>
                        i === idx ? { ...x, slug: e.target.value } : x,
                      ),
                    )
                  }
                />
              </td>
              <td>
                <input
                  type="text"
                  value={m.name}
                  onChange={(e) =>
                    setMemberships(
                      config.memberships.map((x, i) =>
                        i === idx ? { ...x, name: e.target.value } : x,
                      ),
                    )
                  }
                />
              </td>
              <td>
                <select
                  value={m.billing_period}
                  onChange={(e) =>
                    setMemberships(
                      config.memberships.map((x, i) =>
                        i === idx
                          ? {
                              ...x,
                              billing_period: e.target.value as
                                | "monthly"
                                | "quarterly"
                                | "annual",
                            }
                          : x,
                      ),
                    )
                  }
                >
                  <option>monthly</option>
                  <option>quarterly</option>
                  <option>annual</option>
                </select>
              </td>
              <td>
                <input
                  type="number"
                  value={m.price_minor}
                  onChange={(e) =>
                    setMemberships(
                      config.memberships.map((x, i) =>
                        i === idx
                          ? { ...x, price_minor: Number(e.target.value) || 0 }
                          : x,
                      ),
                    )
                  }
                />
              </td>
              <td>
                <input
                  type="text"
                  value={m.currency}
                  onChange={(e) =>
                    setMemberships(
                      config.memberships.map((x, i) =>
                        i === idx ? { ...x, currency: e.target.value } : x,
                      ),
                    )
                  }
                />
              </td>
              <td>
                <input
                  type="text"
                  value={
                    m.sessions_per_cycle === undefined
                      ? ""
                      : String(m.sessions_per_cycle)
                  }
                  placeholder="integer or 'unlimited'"
                  onChange={(e) => {
                    const v = e.target.value;
                    const parsed: number | "unlimited" | undefined =
                      v === ""
                        ? undefined
                        : v === "unlimited"
                          ? "unlimited"
                          : Number(v) || 0;
                    setMemberships(
                      config.memberships.map((x, i) =>
                        i === idx
                          ? (setOrDropAny(
                              x as unknown as Record<string, unknown>,
                              "sessions_per_cycle",
                              parsed,
                            ) as unknown as MembershipOffering)
                          : x,
                      ),
                    );
                  }}
                />
              </td>
              <td>
                <select
                  value={m.session_type ?? ""}
                  onChange={(e) =>
                    setMemberships(
                      config.memberships.map((x, i) =>
                        i === idx
                          ? (setOrDropAny(
                              x as unknown as Record<string, unknown>,
                              "session_type",
                              e.target.value ? e.target.value : undefined,
                            ) as unknown as MembershipOffering)
                          : x,
                      ),
                    )
                  }
                >
                  <option value="">— none —</option>
                  {sessionTypeOptions.map((st) => (
                    <option key={st.slug} value={st.slug}>
                      {st.label} ({st.slug})
                    </option>
                  ))}
                </select>
              </td>
              <td style={{ textAlign: "center" }}>
                <input
                  type="checkbox"
                  aria-label={`Membership ${m.slug} visible to members`}
                  checked={isVisible("memberships", m.slug, "members")}
                  onChange={(e) =>
                    setVisibility(
                      "memberships",
                      m.slug,
                      "members",
                      e.target.checked,
                    )
                  }
                />
              </td>
              <td style={{ textAlign: "center" }}>
                <input
                  type="checkbox"
                  aria-label={`Membership ${m.slug} visible to staff/admin`}
                  checked={isVisible("memberships", m.slug, "staff")}
                  onChange={(e) =>
                    setVisibility(
                      "memberships",
                      m.slug,
                      "staff",
                      e.target.checked,
                    )
                  }
                />
              </td>
              <td>
                <button
                  type="button"
                  onClick={() =>
                    setMemberships(
                      config.memberships.filter((_, i) => i !== idx),
                    )
                  }
                >
                  Remove
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button
        type="button"
        onClick={() =>
          setMemberships([
            ...config.memberships,
            {
              slug: "new-membership",
              name: "New membership",
              billing_period: "monthly",
              price_minor: 0,
              currency: "GBP",
            },
          ])
        }
      >
        + Add membership
      </button>
      {/* Phase 41 followup — cancellation_deadline_hours moved here
          from Business basics. Sits under memberships because it's
          part of the pricing/policy conversation. Defines how many
          hours before slot start a booking can still be cancelled
          with full credit restored. */}
      <h3 style={{ marginTop: 24 }}>Cancellation policy</h3>
      <div className="form-row">
        <label>Cancellation deadline (hours)</label>
        <input
          type="number"
          min={0}
          value={config.cancellation_deadline_hours}
          onChange={(e) =>
            setConfig({
              ...config,
              cancellation_deadline_hours: Number(e.target.value) || 0,
            })
          }
        />
      </div>
      {/* Phase 41 followup — Stripe Checkout default URLs. Lives in the
          pricing section per Mario's UX ask. The `charge_member` action
          applies these whenever the caller (LLM, staff form, CLI)
          omits per-call success_url / cancel_url. Stripe REQUIRES
          success_url; absent at both layers raises
          `stripe_checkout_success_url_unconfigured` → HTTP 412 BEFORE
          the Stripe SDK call. */}
      <h3 style={{ marginTop: 24 }}>Stripe Checkout URLs</h3>
      <p className="subtle" style={{ marginTop: -8 }}>
        Applied by <code>charge_member</code> whenever a per-call value
        isn&apos;t supplied. Stripe requires the success URL — leaving
        it empty makes charges fail with{" "}
        <code>stripe_checkout_success_url_unconfigured</code>. Stripe
        substitutes <code>{"{CHECKOUT_SESSION_ID}"}</code> at redirect
        time, so you can route the customer to a thank-you page that
        loads the session.
      </p>
      <div className="form-row">
        <label>Success URL (https://, max 2048 chars)</label>
        <input
          type="url"
          maxLength={2048}
          placeholder="https://your-studio.com/payment/success?session_id={CHECKOUT_SESSION_ID}"
          value={config.stripe?.checkout_success_url ?? ""}
          onChange={(e) => {
            const next = { ...(config.stripe ?? {}) };
            if (e.target.value.length > 0) {
              next.checkout_success_url = e.target.value;
            } else {
              delete next.checkout_success_url;
            }
            const cfg = { ...config };
            if (
              next.checkout_success_url !== undefined ||
              next.checkout_cancel_url !== undefined
            ) {
              cfg.stripe = next;
            } else {
              delete cfg.stripe;
            }
            setConfig(cfg);
          }}
        />
      </div>
      <div className="form-row">
        <label>Cancel URL (optional · https://, max 2048 chars)</label>
        <input
          type="url"
          maxLength={2048}
          placeholder="https://your-studio.com/payment/cancelled"
          value={config.stripe?.checkout_cancel_url ?? ""}
          onChange={(e) => {
            const next = { ...(config.stripe ?? {}) };
            if (e.target.value.length > 0) {
              next.checkout_cancel_url = e.target.value;
            } else {
              delete next.checkout_cancel_url;
            }
            const cfg = { ...config };
            if (
              next.checkout_success_url !== undefined ||
              next.checkout_cancel_url !== undefined
            ) {
              cfg.stripe = next;
            } else {
              delete cfg.stripe;
            }
            setConfig(cfg);
          }}
        />
      </div>
    </fieldset>
  );
}

function SessionTypesSection({ config, setConfig }: SectionProps): JSX.Element {
  const sessionTypes = config.session_types ?? [];
  const setSessionTypes = (next: SessionType[]) => {
    // Keep `klaviyo.session_type_to_duration_minutes` in sync with the
    // new shape until a follow-up slice migrates the phase-35 resolver
    // to read from `session_types[].klaviyo_label` directly.
    const klaviyoMap: Record<string, number> = {};
    for (const st of next) {
      if (st.klaviyo_label !== undefined && st.klaviyo_label.length > 0) {
        klaviyoMap[st.klaviyo_label] = st.duration_minutes;
      }
    }
    const cfg: StudioL2ConfigShape = { ...config, session_types: next };
    const existingKlaviyo = config.klaviyo ?? {};
    const instructorMap = existingKlaviyo.instructor_name_to_trainer_id;
    if (Object.keys(klaviyoMap).length > 0 || instructorMap !== undefined) {
      cfg.klaviyo = {};
      if (Object.keys(klaviyoMap).length > 0) {
        cfg.klaviyo.session_type_to_duration_minutes = klaviyoMap;
      }
      if (instructorMap !== undefined) {
        cfg.klaviyo.instructor_name_to_trainer_id = instructorMap;
      }
    } else {
      delete cfg.klaviyo;
    }
    setConfig(cfg);
  };
  return (
    <fieldset>
      <legend>Session types</legend>
      <p className="subtle">
        Kinds of session the studio offers, each with a fixed duration.
        Optional <code>klaviyo_label</code> matches the external string
        Klaviyo Flow webhooks emit so the inbound resolver can map back
        to the canonical slug. The Klaviyo block below stays in sync
        automatically.
      </p>
      <table className="config-table">
        <thead>
          <tr>
            <th>slug</th>
            <th>label</th>
            <th>duration_minutes</th>
            <th>klaviyo_label</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {sessionTypes.map((st, idx) => (
            <tr key={idx}>
              <td>
                <input
                  type="text"
                  value={st.slug}
                  onChange={(e) =>
                    setSessionTypes(
                      sessionTypes.map((x, i) =>
                        i === idx ? { ...x, slug: e.target.value } : x,
                      ),
                    )
                  }
                />
              </td>
              <td>
                <input
                  type="text"
                  value={st.label}
                  onChange={(e) =>
                    setSessionTypes(
                      sessionTypes.map((x, i) =>
                        i === idx ? { ...x, label: e.target.value } : x,
                      ),
                    )
                  }
                />
              </td>
              <td>
                <input
                  type="number"
                  min={1}
                  max={480}
                  value={st.duration_minutes}
                  onChange={(e) =>
                    setSessionTypes(
                      sessionTypes.map((x, i) =>
                        i === idx
                          ? {
                              ...x,
                              duration_minutes: Number(e.target.value) || 0,
                            }
                          : x,
                      ),
                    )
                  }
                />
              </td>
              <td>
                <input
                  type="text"
                  value={st.klaviyo_label ?? ""}
                  placeholder="(optional)"
                  onChange={(e) =>
                    setSessionTypes(
                      sessionTypes.map((x, i) =>
                        i === idx
                          ? (setOrDrop(
                              x as unknown as Record<string, unknown>,
                              "klaviyo_label",
                              e.target.value,
                            ) as unknown as SessionType)
                          : x,
                      ),
                    )
                  }
                />
              </td>
              <td>
                <button
                  type="button"
                  onClick={() =>
                    setSessionTypes(sessionTypes.filter((_, i) => i !== idx))
                  }
                >
                  Remove
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button
        type="button"
        onClick={() =>
          setSessionTypes([
            ...sessionTypes,
            {
              slug: "new-session-type",
              label: "New session type",
              duration_minutes: 60,
            },
          ])
        }
      >
        + Add session type
      </button>
    </fieldset>
  );
}

// C1 — per-tier model options for the AI section. `value` is the Anthropic
// model id (validated `^claude-…` + 1..100 chars by the L2 validator and
// `resolveChatModel` in the saga). "" ⇒ the field is dropped and the tier
// inherits the client default (Sonnet 4.6). Keep this list to models that
// make sense for a high-volume chat agent (Fable/Opus-Fable tiers omitted —
// far pricier with no booking-accuracy upside here).
const CHAT_MODEL_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "", label: "Default — Sonnet 4.6" },
  { value: "claude-haiku-4-5", label: "Haiku 4.5 — fastest & cheapest" },
  { value: "claude-sonnet-4-6", label: "Sonnet 4.6 — balanced (default)" },
  { value: "claude-sonnet-5", label: "Sonnet 5 — near-Opus quality, agentic" },
  { value: "claude-opus-4-8", label: "Opus 4.8 — most capable, slowest" },
];

function AiVoiceSection({ config, setConfig }: SectionProps): JSX.Element {
  const studio = config.studio ?? {};
  const ct = config.chat_translator ?? {};
  return (
    <fieldset>
      <legend>AI</legend>
      <div className="form-row">
        <label>Brand voice (max 2000 chars)</label>
        <textarea
          maxLength={2000}
          rows={5}
          value={studio.brand_voice ?? ""}
          onChange={(e) =>
            setConfig({
              ...config,
              studio: setOrDrop(studio, "brand_voice", e.target.value),
            })
          }
        />
      </div>
      <div className="form-row">
        <label>Onboarding guidance (max 500 chars; new members only)</label>
        <textarea
          maxLength={500}
          rows={3}
          value={ct.onboarding_guidance ?? ""}
          onChange={(e) =>
            setConfig({
              ...config,
              chat_translator: setOrDrop(
                ct,
                "onboarding_guidance",
                e.target.value,
              ),
            })
          }
        />
      </div>
      <div className="form-row">
        <label>Max LLM calls per member per 24h (default 50, range 1..1000)</label>
        <input
          type="number"
          min={1}
          max={1000}
          value={ct.max_llm_calls_per_member_per_24h ?? ""}
          placeholder="50"
          onChange={(e) =>
            setConfig({
              ...config,
              chat_translator: setOrDropAny(
                ct,
                "max_llm_calls_per_member_per_24h",
                e.target.value ? Number(e.target.value) : undefined,
              ),
            })
          }
        />
      </div>
      <div className="form-row">
        <label>Staff wait before responding (sec; default 0 = immediate)</label>
        <input
          type="number"
          min={0}
          max={120}
          value={ct.debounce_seconds_staff ?? ""}
          placeholder="0"
          onChange={(e) =>
            setConfig({
              ...config,
              chat_translator: setOrDropAny(
                ct,
                "debounce_seconds_staff",
                e.target.value !== "" ? Number(e.target.value) : undefined,
              ),
            })
          }
        />
        <small className="subtle">
          Staff messages are answered immediately (0 = no wait). A single
          Inngest function can't apply a separate non-zero staff window, so
          staff always bypass the debounce regardless of this value.
        </small>
      </div>
      <div className="form-row">
        <label>Member wait before responding (sec; default 5, range 0..120)</label>
        <input
          type="number"
          min={0}
          max={120}
          value={ct.debounce_seconds_member ?? ""}
          placeholder="5"
          onChange={(e) =>
            setConfig({
              ...config,
              chat_translator: setOrDropAny(
                ct,
                "debounce_seconds_member",
                e.target.value !== "" ? Number(e.target.value) : undefined,
              ),
            })
          }
        />
        <small className="subtle">
          Inngest collapses a member's rapid back-to-back messages into one
          LLM call within this window. Change takes effect on next receiver
          restart.
        </small>
      </div>

      <h3 style={{ marginTop: 24 }}>Model per agent</h3>
      <p className="subtle" style={{ marginTop: -8 }}>
        Which Claude model each chat tier uses. Default is Sonnet 4.6. Haiku
        is markedly faster and cheaper — a good fit for members; keep Staff on
        Sonnet for the trickier multi-step admin flows. The model is resolved
        once per conversation (stable across turns to keep the prompt cache),
        so a change applies to the next new conversation — no redeploy. The
        Staff setting also covers admin staff.
      </p>
      <div className="form-row">
        <label>Member (client) agent</label>
        <select
          value={ct.model_member ?? ""}
          onChange={(e) =>
            setConfig({
              ...config,
              chat_translator: setOrDropAny(
                ct,
                "model_member",
                e.target.value || undefined,
              ),
            })
          }
        >
          {CHAT_MODEL_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
      <div className="form-row">
        <label>Staff agent (incl. admin)</label>
        <select
          value={ct.model_staff ?? ""}
          onChange={(e) =>
            setConfig({
              ...config,
              chat_translator: setOrDropAny(
                ct,
                "model_staff",
                e.target.value || undefined,
              ),
            })
          }
        >
          {CHAT_MODEL_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
    </fieldset>
  );
}

// Per-person memory section (phases 57 + 61). The studio-wide preambles
// prepended to every member/trainer's individual memory at chat-translator
// injection, the three LLM generation prompts used by the daily-writer +
// recycler crons (blank = the shipped byte-identical defaults), and the
// recycle budgets. The two cron schedules are DISPLAY-ONLY (sub-decision G):
// the live cron is one global Inngest function set at deploy via
// LAYER_MEMORY_*_CRON; editing the YAML value does not reschedule it.
// ---------------------------------------------------------------------------
// Memory section default prompts — byte-identical mirrors of the canonical
// source of truth in `packages/canonical-data/src/l2-yaml.ts`
// (MEMORY_DEFAULT_DAILY_SUMMARY_MEMBER_PROMPT / _STAFF_PROMPT /
// MEMORY_DEFAULT_RECYCLER_PROMPT). Duplicated here because the browser bundle
// must not take a runtime dep on the canonical-data package (see the file
// header). If you change a default there, change it here too.
// ---------------------------------------------------------------------------

const MEMORY_DEFAULT_MEMBER_PROMPT =
  "You write concise, factual daily memory notes about a fitness studio's member, to be read by the studio's AI assistant before future conversations. Summarize the most important things from today's conversation in 2-4 sentences: what they wanted, what was done, any preferences/quirks observed, and anything worth remembering next time. Write plain prose — no headers, no bullet lists, no preamble like 'Here is a summary'.";

const MEMORY_DEFAULT_STAFF_PROMPT =
  "You write concise, factual daily activity notes about a fitness studio's trainer/staff member, to be read by the studio's AI assistant. Summarize what they did today in 1-3 sentences based on their logged actions. Write plain prose — no headers, no bullet lists, no preamble.";

const MEMORY_DEFAULT_RECYCLER_PROMPT =
  "You maintain a bounded long-term memory for a fitness studio's AI assistant. You are given the EXISTING long-term memory plus the recent rolling daily entries. Integrate the new material into the long-term memory: keep recent detail concrete, progressively abstract older detail, and REMOVE redundancy. Then write a single distillation paragraph that captures the most important durable facts. Respond with EXACTLY two sections, each introduced by a header line on its own:\n===LONGTERM===\n<the updated long-term memory>\n===SUMMARY===\n<the single distillation paragraph>\nNo other text.";

/**
 * Render a known cron expression in plain English. Handles the two shapes the
 * memory crons use (`m h * * *` daily, `m h D * *` monthly-on-day-D); falls
 * back to the raw expression for anything else.
 */
function humanizeCron(expr: string): string {
  const parts = expr.trim().split(/\s+/);
  if (parts.length !== 5) return expr;
  const [min, hr, dom, mon, dow] = parts as [
    string,
    string,
    string,
    string,
    string,
  ];
  const m = Number(min);
  const h = Number(hr);
  if (!Number.isInteger(m) || !Number.isInteger(h)) return expr;
  const hhmm = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")} UTC`;
  if (dom === "*" && mon === "*" && dow === "*") {
    return `Every day at ${hhmm}`;
  }
  if (mon === "*" && dow === "*" && /^\d+$/.test(dom)) {
    const d = Number(dom);
    const ord =
      d === 1
        ? "1st"
        : d === 2
          ? "2nd"
          : d === 3
            ? "3rd"
            : d === 21
              ? "21st"
              : d === 22
                ? "22nd"
                : d === 23
                  ? "23rd"
                  : d === 31
                    ? "31st"
                    : `${d}th`;
    return `On the ${ord} of every month at ${hhmm}`;
  }
  return expr;
}

function MemorySection({ config, setConfig }: SectionProps): JSX.Element {
  const mem = config.memory ?? {};
  const dailyWriterCron = mem.daily_writer_cron ?? "30 2 * * *";
  const recyclerCron = mem.recycler_cron ?? "0 3 1 * *";
  return (
    <fieldset>
      <legend>Memory</legend>
      <p className="subtle">
        Studio-wide context prepended to every person&apos;s individual memory,
        plus the prompts the nightly/monthly memory crons use. Leave a prompt
        blank to use the shipped default.
      </p>

      <div className="form-row">
        <label>Member preamble (max 4000 chars)</label>
        <textarea
          maxLength={4000}
          rows={6}
          value={mem.member_preamble ?? ""}
          onChange={(e) =>
            setConfig({
              ...config,
              memory: setOrDrop(mem, "member_preamble", e.target.value),
            })
          }
        />
      </div>
      <div className="form-row">
        <label>Staff / trainer preamble (max 4000 chars)</label>
        <textarea
          maxLength={4000}
          rows={6}
          value={mem.staff_preamble ?? ""}
          onChange={(e) =>
            setConfig({
              ...config,
              memory: setOrDrop(mem, "staff_preamble", e.target.value),
            })
          }
        />
      </div>

      <div className="form-row">
        <label>
          Daily summary prompt — members (blank = use shipped default below;
          max 8000)
        </label>
        <textarea
          maxLength={8000}
          rows={6}
          placeholder={MEMORY_DEFAULT_MEMBER_PROMPT}
          value={mem.daily_summary_member_prompt ?? ""}
          onChange={(e) =>
            setConfig({
              ...config,
              memory: setOrDrop(
                mem,
                "daily_summary_member_prompt",
                e.target.value,
              ),
            })
          }
        />
        {(mem.daily_summary_member_prompt ?? "").length === 0 && (
          <button
            type="button"
            className="secondary small"
            onClick={() =>
              setConfig({
                ...config,
                memory: setOrDrop(
                  mem,
                  "daily_summary_member_prompt",
                  MEMORY_DEFAULT_MEMBER_PROMPT,
                ),
              })
            }
          >
            Load default into editor ↑
          </button>
        )}
      </div>
      <div className="form-row">
        <label>
          Daily summary prompt — staff (blank = use shipped default below; max
          8000)
        </label>
        <textarea
          maxLength={8000}
          rows={6}
          placeholder={MEMORY_DEFAULT_STAFF_PROMPT}
          value={mem.daily_summary_staff_prompt ?? ""}
          onChange={(e) =>
            setConfig({
              ...config,
              memory: setOrDrop(
                mem,
                "daily_summary_staff_prompt",
                e.target.value,
              ),
            })
          }
        />
        {(mem.daily_summary_staff_prompt ?? "").length === 0 && (
          <button
            type="button"
            className="secondary small"
            onClick={() =>
              setConfig({
                ...config,
                memory: setOrDrop(
                  mem,
                  "daily_summary_staff_prompt",
                  MEMORY_DEFAULT_STAFF_PROMPT,
                ),
              })
            }
          >
            Load default into editor ↑
          </button>
        )}
      </div>
      <div className="form-row">
        <label>
          Recycler prompt — monthly fold &amp; distil (blank = use shipped
          default below; max 8000)
        </label>
        <textarea
          maxLength={8000}
          rows={8}
          placeholder={MEMORY_DEFAULT_RECYCLER_PROMPT}
          value={mem.recycler_prompt ?? ""}
          onChange={(e) =>
            setConfig({
              ...config,
              memory: setOrDrop(mem, "recycler_prompt", e.target.value),
            })
          }
        />
        {(mem.recycler_prompt ?? "").length === 0 && (
          <button
            type="button"
            className="secondary small"
            onClick={() =>
              setConfig({
                ...config,
                memory: setOrDrop(
                  mem,
                  "recycler_prompt",
                  MEMORY_DEFAULT_RECYCLER_PROMPT,
                ),
              })
            }
          >
            Load default into editor ↑
          </button>
        )}
      </div>

      <div className="form-row">
        <label>Rolling retention (days; default 30, range 1..365)</label>
        <input
          type="number"
          min={1}
          max={365}
          placeholder="30"
          value={mem.rolling_retention_days ?? ""}
          onChange={(e) =>
            setConfig({
              ...config,
              memory: setOrDropAny(
                mem,
                "rolling_retention_days",
                e.target.value ? Number(e.target.value) : undefined,
              ),
            })
          }
        />
      </div>
      <div className="form-row">
        <label>Long-term token budget (default 6000, range 1..100000)</label>
        <input
          type="number"
          min={1}
          max={100000}
          placeholder="6000"
          value={mem.longterm_token_budget ?? ""}
          onChange={(e) =>
            setConfig({
              ...config,
              memory: setOrDropAny(
                mem,
                "longterm_token_budget",
                e.target.value ? Number(e.target.value) : undefined,
              ),
            })
          }
        />
      </div>

      <div className="form-row">
        <label>Cron schedules (informational — read-only)</label>
        <ul className="subtle" style={{ margin: "0.25rem 0", paddingLeft: "1.2rem" }}>
          <li>
            <strong>Daily writer</strong> — {humanizeCron(dailyWriterCron)}{" "}
            <code>({dailyWriterCron})</code>
          </li>
          <li>
            <strong>Monthly recycler</strong> — {humanizeCron(recyclerCron)}{" "}
            <code>({recyclerCron})</code>
          </li>
        </ul>
        <small className="subtle">
          Display-only. The live schedule is one global cron set at deploy via
          LAYER_MEMORY_DAILY_WRITER_CRON / LAYER_MEMORY_RECYCLER_CRON; editing
          here does not reschedule it.
        </small>
      </div>
    </fieldset>
  );
}

// Migration 042 — DB-backed staff allowlist editor. Self-contained (fetches
// its own rows from `/api/staff-allowlist/:slug`, saves back to the same
// endpoint) so a Save here is LIVE on the next inbound message — independent
// of the YAML config Save flow. The `admin` checkbox is the phase-64 tier.
//
// The `studio.trainer_comms_admin_mode` radios lived here until 2026-08-29;
// they now render in the Trainers pane (`TrainerCommsRoutingBox`), where the
// people they route messages to are listed. `onOpenTrainers` powers the
// pointer this section leaves behind.

// Migration 192 — the per-person notification grid's display vocabulary.
// Keep the three consts in LOCKSTEP with `STAFF_NOTIFICATION_GRID_TYPES` /
// `STAFF_NOTIFICATION_CHANNEL_CAPABILITIES` in
// packages/canonical-data/src/l2-yaml.ts — the action's JSON Schema rejects
// any tick outside the capability map, so drift here surfaces as a 400 on
// save, never a silent success. Tuples: [type, label, hint].
const NOTIFICATION_GRID_TYPES = [
  [
    "payments",
    "Payment alerts",
    "Payment received / failed / refunded FYIs, incl. membership-payment issues and dunning.",
  ],
  ["bookings", "Booking alerts", "Booking created / cancelled FYIs."],
  ["members", "New-member alerts", "New member / lead FYIs."],
  [
    "handoff",
    "“Talk to a human” escalations",
    "Paged when a member asks to speak to a human — including the '💬 Talk to a human' button in chat. If nobody has this ticked anywhere, unmuted admins are paged so a request never strands.",
  ],
  [
    "unanswered",
    "Unanswered-message alerts",
    "Paged when a message arrives on a channel the assistant isn't answering — Instagram/email until the member's AI pill is set, WhatsApp when the studio's AI is off. At most once per sender per day. Same nobody-ticked → unmuted-admins fallback as handoff.",
  ],
  [
    "home_session",
    "At-home session requests",
    "The chat agent never books home sessions — it escalates them. Same nobody-ticked → unmuted-admins fallback as handoff.",
  ],
  [
    "enquiries",
    "Website contact-form enquiries",
    "Paged when someone sends the website contact form, with what they wrote. Fires whether or not they are already a member — unlike New-member alerts, which announce the row and stay silent for an existing member. Same nobody-ticked → unmuted-admins fallback as handoff.",
  ],
  [
    "instagram",
    "Instagram alerts",
    "DM notifier + comment/mention router.",
  ],
  ["rota", "Rota alerts", "Coverage-watchdog gap alerts."],
  [
    "ratings",
    "Session-rating alerts",
    "Fires on EVERY rating, comment included — not just bad ones.",
  ],
  [
    "digests",
    "Daily digests (admin-only)",
    "Churn / renewal / chat-config nudge / chat-efficiency / tasks morning. These lanes page admins only — for a non-admin the tick is inert.",
  ],
  [
    "infra",
    "Infrastructure alerts (admin-only)",
    "DLQ / idempotency / integration health / inbox alarm / watchdog notify legs. Admin-only lanes — for a non-admin the tick is inert.",
  ],
  [
    "checkout",
    "Abandoned-checkout alerts",
    "The plain abandoned/expired checkout + failed-payment alerts.",
  ],
  [
    "checkout_leads",
    "Checkout lead cards (approve/reject)",
    "The 'UNKNOWN buyer' card with APPROVE/REJECT buttons that creates a lead + sends the recovery email. First tap wins; later taps see 'Already handled.'",
  ],
  [
    "checkout_digest",
    "Daily checkout-funnel digest",
    "The daily 07:30 purchase-funnel digest.",
  ],
  [
    "error",
    "Error notifications",
    "The 15-min error-alert digest (audit_error_alerter). WhatsApp-only by capability.",
  ],
  [
    "decisions",
    "Staff-decision ballots",
    "Where your ballots arrive. 'Takes part in staff decisions' below decides WHO votes; this decides WHERE. Ballots bypass the mute — the mute governs FYIs, not decisions.",
  ],
] as const;

const NOTIFICATION_GRID_CHANNELS = [
  ["whatsapp", "WhatsApp", "needs a phone number on this row"],
  ["telegram", "Telegram", "needs the telegram chat id above"],
  ["email", "Email", "needs the notify email above"],
] as const;

// Which types each channel section lists — the connector's actual reach.
// Telegram has no error lane (WhatsApp-template-only); email carries only
// the event classes (no checkout/error/decisions machinery).
const NOTIFICATION_GRID_CAPABILITIES: Record<string, ReadonlyArray<string>> = {
  whatsapp: NOTIFICATION_GRID_TYPES.map(([t]) => t),
  telegram: NOTIFICATION_GRID_TYPES.map(([t]) => t).filter(
    (t) => t !== "error",
  ),
  email: [
    "payments",
    "bookings",
    "members",
    "handoff",
    "unanswered",
    "home_session",
    "enquiries",
    "instagram",
    "rota",
    "ratings",
    "digests",
    "infra",
  ],
};
/**
 * One staff member's full settings, behind the roster's "Settings" expander.
 *
 * WHY A PANEL AND NOT MORE COLUMNS. The allowlist card reached SIXTEEN columns
 * — phone, staff_id, name, email, telegram id, login email, admin, mute, six
 * notification rails, voter — each one a two-letter header with the meaning
 * hidden in a `title` tooltip. Page access would have been the seventeenth, and
 * it is a LIST, not a checkbox. A table row is the wrong shape for a person:
 * the roster now answers "who is staff, who is an admin, who is muted, how much
 * can they see", and everything that belongs to one individual lives here where
 * there is room to say what it means.
 *
 * The panel is presentation only. Every write goes back through the section's
 * existing handlers — `update()` for text (batched into the explicit Save) and
 * `toggleFlag()` for checkboxes (instant-save against the last SERVER-CONFIRMED
 * list). Reproducing either here would reintroduce the 2026-08-06 bug where a
 * flag toggle committed another row's half-typed phone.
 */
function StaffSettingsPanel({
  entry,
  saving,
  onText,
  onFlag,
  onGrid,
  onPages,
}: {
  readonly entry: StaffAllowlistEntry;
  readonly saving: boolean;
  readonly onText: (patch: Partial<StaffAllowlistEntry>) => void;
  readonly onFlag: (
    field: StaffAllowlistFlagField,
    checked: boolean,
    label: string,
  ) => void;
  readonly onGrid: (
    gridType: string,
    channel: string,
    checked: boolean,
    label: string,
  ) => void;
  readonly onPages: (next: string[] | null) => void;
}): JSX.Element {
  const granted = entry.visible_pages ?? null;
  const groups = useMemo(() => {
    const byGroup = new Map<string, typeof DASHBOARD_PAGES>();
    for (const page of DASHBOARD_PAGES) {
      byGroup.set(page.group, [...(byGroup.get(page.group) ?? []), page]);
    }
    return [...byGroup.entries()];
  }, []);

  // Ticking a box on an UNRESTRICTED person has to start from "everything",
  // not from "nothing" — otherwise unticking one page would silently revoke
  // the other 22. So the first interaction materialises the full list and
  // removes the one page, which is what the operator's single click meant.
  const setPage = (id: string, on: boolean): void => {
    const base = granted ?? DASHBOARD_PAGES.map((p) => p.id);
    const next = on
      ? [...new Set([...base, id])]
      : base.filter((x) => x !== id);
    onPages(next.sort());
  };

  return (
    <div className="staff-settings">
      <div className="staff-settings-grid">
        <label>
          staff_id (UUID v7)
          <input
            type="text"
            value={entry.staff_id}
            onChange={(ev) => onText({ staff_id: ev.target.value })}
          />
        </label>
        <label>
          login email
          <input
            type="text"
            inputMode="email"
            placeholder="not bound"
            value={entry.login_email ?? ""}
            onChange={(ev) => onText({ login_email: ev.target.value })}
          />
          <span className="subtle">
            The address they <strong>sign in</strong> with. Not the same as the
            notify email below — this is how the dashboard knows which row a
            logged-in person is.
          </span>
        </label>
        <label>
          notify email
          <input
            type="text"
            inputMode="email"
            placeholder="none"
            value={entry.email ?? ""}
            onChange={(ev) => onText({ email: ev.target.value })}
          />
          <span className="subtle">
            Where <code>notify_staff</code> writes to them. Empty = no email
            alerts.
          </span>
        </label>
        <label>
          telegram chat id
          <input
            type="text"
            inputMode="numeric"
            placeholder="none"
            value={entry.telegram_chat_id ?? ""}
            onChange={(ev) => onText({ telegram_chat_id: ev.target.value })}
          />
          <span className="subtle">
            Used both ways — recognises their inbound Telegram messages as
            staff, and receives outbound alerts. A group id starts with a minus.
          </span>
        </label>
      </div>

      <fieldset className="staff-settings-block">
        <legend>Notifications</legend>
        <p className="subtle">
          Migration 192 — one section per channel, each listing only what that
          channel can deliver. Tick a box and this person gets that
          notification type on that channel; a type with nothing ticked
          anywhere is <strong>off</strong> for them. Event alerts reach every
          unmuted person per their ticks — Admin is not required; the two
          admin-only types additionally need the Admin box.{" "}
          <strong>Don&apos;t send messages</strong> on the roster still
          silences everything regardless (ballots excepted). All save
          instantly.
        </p>
        {NOTIFICATION_GRID_CHANNELS.map(([channel, chLabel, addressHint]) => {
          const address =
            channel === "whatsapp"
              ? entry.phone
              : channel === "telegram"
                ? entry.telegram_chat_id
                : entry.email;
          const addressMissing = (address ?? "").trim() === "";
          return (
            <div key={channel} className="staff-settings-grid-section">
              <div>
                <strong>{chLabel}</strong>{" "}
                {addressMissing && (
                  <span className="tag tag-warn">
                    {addressHint} — ticks here won&apos;t deliver until it is
                    set
                  </span>
                )}
              </div>
              <div className="staff-settings-flags">
                {NOTIFICATION_GRID_TYPES.filter(([t]) =>
                  NOTIFICATION_GRID_CAPABILITIES[channel]?.includes(t),
                ).map(([t, label, hint]) => (
                  <label key={t} title={hint}>
                    <input
                      type="checkbox"
                      checked={(entry.notification_grid?.[t] ?? []).includes(
                        channel,
                      )}
                      disabled={saving}
                      onChange={(ev) =>
                        onGrid(
                          t,
                          channel,
                          ev.target.checked,
                          `${chLabel} · ${label}`,
                        )
                      }
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>
          );
        })}
        <div className="staff-settings-flags">
          <label>
            <input
              type="checkbox"
              checked={entry.voter === true}
              disabled={saving}
              onChange={(ev) => onFlag("voter", ev.target.checked, "Voter change")}
            />
            Takes part in staff decisions
          </label>
        </div>
      </fieldset>

      <fieldset className="staff-settings-block">
        <legend>Dashboard pages</legend>
        <p className="subtle">
          {granted === null ? (
            <>
              <strong>Unrestricted</strong> — this person can open every page.
              Untick any page below to start restricting them.
            </>
          ) : (
            <>
              Restricted to <strong>{granted.length}</strong> of{" "}
              {DASHBOARD_PAGES.length} pages. <strong>Home</strong> is always
              visible — it is where a blocked page sends them.
            </>
          )}{" "}
          This controls what they can <strong>see</strong>. What they can{" "}
          <strong>do</strong> is still governed by the <strong>admin</strong>{" "}
          tick and each action&apos;s own rules, so hiding Payroll hides the
          page, not every underlying figure.{" "}
          <strong>Saves with the button below</strong>, not instantly.
        </p>
        {granted !== null && (
          <p>
            <button
              type="button"
              className="linklike"
              onClick={() => onPages(null)}
            >
              Remove all restrictions
            </button>
          </p>
        )}
        <div className="staff-pages-grid">
          {groups.map(([group, pages]) => (
            <div key={group} className="staff-pages-group">
              <div className="staff-pages-group-head">{group}</div>
              {pages.map((page) => {
                const always = page.alwaysAllowed === true;
                return (
                  <label key={page.id}>
                    <input
                      type="checkbox"
                      checked={always || granted === null || granted.includes(page.id)}
                      // Home cannot be unticked: it is the fail-closed landing
                      // page and the redirect target, so removing it would be a
                      // loop into a blank product.
                      disabled={always}
                      onChange={(ev) => setPage(page.id, ev.target.checked)}
                    />
                    {page.label}
                    {always ? <span className="subtle"> (always)</span> : null}
                  </label>
                );
              })}
            </div>
          ))}
        </div>
      </fieldset>
    </div>
  );
}

function StaffAllowlistSection({
  slug,
  onOpenTrainers,
}: {
  slug: string;
  onOpenTrainers: () => void;
}): JSX.Element {
  const [entries, setEntries] = useState<StaffAllowlistEntry[]>([]);
  // 2026-08-06 — the last SERVER-CONFIRMED list. An admin-checkbox toggle saves
  // against this, never against `entries`, which holds every keystroke of the
  // text fields. See `lib/staff-allowlist-edit.ts` for the incident this fixes.
  const [savedEntries, setSavedEntries] = useState<StaffAllowlistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  // Which row's Settings panel is open. ONE at a time, by index: the panel is
  // tall (four identity fields, three notification-grid sections, a 24-page
  // tick-list), and two open at once would push the second one off screen
  // with no hint that the roster continues below it.
  const [expanded, setExpanded] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .getStaffAllowlist(slug)
      .then((resp) => {
        if (cancelled) return;
        const loaded = resp.entries.map((e) => ({
          phone: e.phone,
          staff_id: e.staff_id,
          display_name: e.display_name,
          admin: e.admin,
          alerts_muted: e.alerts_muted === true,
          voter: e.voter === true,
          // Migration 192 — the grid always arrives (NOT NULL column); the
          // `?? {}` covers only a malformed response.
          notification_grid: e.notification_grid ?? {},
          email: e.email,
          telegram_chat_id: e.telegram_chat_id,
          login_email: e.login_email,
          visible_pages: e.visible_pages,
        }));
        setEntries(loaded);
        setSavedEntries(loaded);
        setErr(null);
      })
      .catch((e: unknown) => {
        if (!cancelled) setErr((e as Error).message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const update = (idx: number, patch: Partial<StaffAllowlistEntry>) =>
    setEntries(entries.map((x, i) => (i === idx ? { ...x, ...patch } : x)));

  // Persist the given list to the staff_allowlist table. `next` is passed
  // explicitly so callers (e.g. an instant checkbox toggle) save the NEW
  // state without waiting for a React re-render.
  const persist = async (
    next: StaffAllowlistEntry[],
    // Non-null ⇒ this save came from an instant flag checkbox, and names it for
    // the confirmation message ("Admin change saved" / "Messages setting
    // saved"). Null ⇒ the explicit "Save allowlist" button.
    flagLabel: string | null = null,
  ): Promise<void> => {
    setSaving(true);
    setErr(null);
    setMsg(null);
    try {
      const resp = await api.saveStaffAllowlist(
        slug,
        next.map((e) => ({
          phone: e.phone,
          staff_id: e.staff_id,
          display_name: e.display_name,
          admin: e.admin === true,
          alerts_muted: e.alerts_muted === true,
          voter: e.voter === true,
          // Migration 192 — sent verbatim when the row carries one ({} is a
          // real value: everything off). OMITTED when it does not, so the
          // server keeps the stored grid rather than having this client
          // manufacture one — the stale-tab guard.
          ...(e.notification_grid !== undefined
            ? { notification_grid: e.notification_grid }
            : {}),
          // Sent as `?? null` — the form always knows the value now, so "" and
          // null both mean CLEAR and there is no third state to preserve.
          email: e.email ?? null,
          telegram_chat_id: e.telegram_chat_id ?? null,
          // Migration 177 — sent explicitly like the two above. The server's
          // carry-over exists for callers that do not know the column; this
          // form does, so "always send what the box shows" is the shape that
          // cannot surprise (an empty box maps to null = CLEAR server-side).
          login_email: e.login_email ?? null,
          // Migration 178 — `?? null` and never `|| null`: the EMPTY array is
          // "restricted to nothing" and a truthiness test would send it as
          // `null`, i.e. UNRESTRICTED — the exact inverse of unticking every
          // page.
          visible_pages: e.visible_pages ?? null,
        })),
      );
      const confirmed = resp.entries.map((e) => ({
        phone: e.phone,
        staff_id: e.staff_id,
        display_name: e.display_name,
        admin: e.admin,
        alerts_muted: e.alerts_muted === true,
        voter: e.voter === true,
        notification_grid: e.notification_grid ?? {},
        email: e.email,
        telegram_chat_id: e.telegram_chat_id,
        login_email: e.login_email,
        visible_pages: e.visible_pages,
      }));
      setSavedEntries(confirmed);
      if (flagLabel !== null) {
        // Keep the operator's in-progress text; take only the flags the server
        // just confirmed. Clobbering `entries` here is what made a retyped
        // phone race the next save.
        setEntries((current) => mergeFlags(current, confirmed));
        setMsg(
          hasUnsavedTextEdits(entries, confirmed)
            ? `${flagLabel} saved — you have unsaved edits to phone/name; click Save allowlist to apply them.`
            : `${flagLabel} saved — live on the next inbound message.`,
        );
      } else {
        setEntries(confirmed);
        setMsg(
          `Saved ${resp.entries.length} ${
            resp.entries.length === 1 ? "entry" : "entries"
          } — live on the next inbound message.`,
        );
      }
    } catch (e: unknown) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const save = (): Promise<void> => persist(entries);

  // Instant-save the admin tier on toggle — no separate Save click. Other
  // fields (phone/staff_id/display_name) still persist via "Save allowlist"
  // so we don't write on every keystroke.
  //
  // 2026-08-06 — that last sentence used to be false. The POST is a full-list
  // replace, and this handler built it from `entries`, which holds every
  // keystroke — so ticking one row's box committed another row's half-typed
  // phone. Three corrupted operator numbers in one day, each a valid E.164 that
  // no validation could reject. It now saves against the last SERVER-CONFIRMED
  // list, so a toggle can only ever change an admin bit.
  //
  // Migration 160 — generalised over the two flag columns. `alerts_muted` runs
  // the SAME path as `admin` deliberately: it is the one that most needs to
  // work on the first click, since the person asking for it has usually already
  // asked once and been ignored.
  const toggleFlag = (
    idx: number,
    field: StaffAllowlistFlagField,
    checked: boolean,
    label: string,
  ): void => {
    const next = buildFlagToggleList(savedEntries, entries, idx, field, checked);
    // Reflect the tick immediately; text edits stay untouched in `entries`.
    setEntries((current) =>
      current.map((x, i) => (i === idx ? { ...x, [field]: checked } : x)),
    );
    void persist(next, label);
  };

  // Migration 192 — a grid tick runs the SAME instant-save discipline as a
  // flag: the POST is built from the last server-confirmed list (only the
  // toggled cell differs), while the local reflect applies the identical
  // cell flip via the shared `withGridCell` so the two can never disagree.
  const toggleGridCell = (
    idx: number,
    gridType: string,
    channel: string,
    checked: boolean,
    label: string,
  ): void => {
    const next = buildGridToggleList(
      savedEntries,
      entries,
      idx,
      gridType,
      channel,
      checked,
    );
    setEntries((current) =>
      current.map((x, i) =>
        i === idx
          ? {
              ...x,
              notification_grid: withGridCell(
                x.notification_grid,
                gridType,
                channel,
                checked,
              ),
            }
          : x,
      ),
    );
    void persist(next, label);
  };

  // Migration 177 — rows with no dashboard login bound. Surfaced as a standing
  // warning rather than left to be discovered, because the consequence is
  // asymmetric: today an unbound row is merely untidy, but the per-staff page
  // gate resolves a browser session THROUGH this column and is fail-closed, so
  // the same rows become people who can see nothing. Naming them now is what
  // makes that a filled-in field instead of a support call.
  const unboundNames = savedEntries
    .filter((e) => (e.login_email ?? "").trim() === "")
    .map((e) => (e.display_name.trim() === "" ? "(unnamed)" : e.display_name));

  return (
    <fieldset>
      <legend>Staff allowlist · WhatsApp → human:staff promotion (live)</legend>
      <p className="subtle">
        Stored in the <code>staff_allowlist</code> table — saving here takes
        effect on the next inbound WhatsApp message (no redeploy).{" "}
        <strong>Admin</strong> grants the full chat tool catalogue (incl.
        destructive actions) and makes this person a{" "}
        <em>managers-audience</em> recipient — the admin-only lanes (daily
        digests, watchdogs, ops alerts) go to Admin rows only; unchecked =
        normal staff, allowlist-filtered like a member.{" "}
        <strong>Don&apos;t send messages</strong> stops every automated
        message to that person — <code>notify_staff</code> skips them on
        every channel, for every alert and digest. It is{" "}
        <em>independent of admin</em>: a muted admin keeps all their rights and
        simply hears nothing, so you never have to demote someone just to give
        them peace. What each person receives, and on which channel, is their{" "}
        <strong>notification grid</strong> — the three channel sections in
        each row&apos;s Settings panel; a type with nothing ticked anywhere is
        off for them, and the mute still silences everything. Event FYIs
        (payment / booking / new-member alerts) reach <em>every</em> unmuted
        person per their ticks, Admin or not. <strong>Voter</strong> means
        this person takes part in staff decisions — ballots reach voters even
        when muted, because the mute governs FYI alerts, not decisions.{" "}
        <strong>login email</strong>{" "}
        is the address this person signs in to <em>this dashboard</em> with —
        deliberately a different field from <strong>email</strong>, which is only
        where <code>notify_staff</code> writes to them (on this studio the two
        really are different addresses). It is how a browser session is matched
        back to a row here, so leave it blank and the dashboard cannot tell who
        is looking. All <strong>checkboxes save instantly</strong> on toggle —
        the phone / staff_id / display_name / email / telegram / login email
        fields save with the button below.
      </p>
      <p className="subtle" style={{ margin: "0.5rem 0 0.75rem" }}>
        Trainer comms routing — who receives the morning brief and the
        post-session prompt — lives in the{" "}
        <button type="button" className="link-button" onClick={onOpenTrainers}>
          Trainers
        </button>{" "}
        section.
      </p>
      {err !== null && <div className="tag tag-danger">Error: {err}</div>}
      {msg !== null && <div className="tag tag-success">{msg}</div>}
      {!loading && unboundNames.length > 0 && (
        <div className="tag tag-warn">
          No dashboard login bound for {unboundNames.join(", ")} — fill in{" "}
          <strong>login email</strong> with the address they actually sign in
          with. Until then the dashboard cannot tell which row a logged-in
          person is, which is what per-staff page access will depend on.
        </div>
      )}
      {loading ? (
        <p className="subtle">Loading…</p>
      ) : (
        <>
          <table className="config-table staff-roster">
            <thead>
              <tr>
                <th>name</th>
                <th>phone (E.164)</th>
                <th>admin</th>
                <th title="Migration 160 — when ticked, notify_staff never sends this person anything, on any channel. Independent of admin.">
                  don&apos;t send
                  <br />
                  messages
                </th>
                <th title="How many dashboard pages this person can open. 'all' = unrestricted, which is every person's default.">
                  pages
                </th>
                <th></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e, idx) => {
                const open = expanded === idx;
                const granted = e.visible_pages ?? null;
                const pageLabel =
                  granted === null
                    ? "all"
                    : `${granted.length}/${DASHBOARD_PAGES.length}`;
                return (
                  <Fragment key={idx}>
                    <tr>
                      <td>
                        <input
                          type="text"
                          value={e.display_name}
                          placeholder="name"
                          onChange={(ev) =>
                            update(idx, { display_name: ev.target.value })
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={e.phone}
                          onChange={(ev) => update(idx, { phone: ev.target.value })}
                        />
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <input
                          type="checkbox"
                          checked={e.admin === true}
                          disabled={saving}
                          onChange={(ev) =>
                            toggleFlag(idx, "admin", ev.target.checked, "Admin change")
                          }
                        />
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <input
                          type="checkbox"
                          checked={e.alerts_muted === true}
                          disabled={saving}
                          onChange={(ev) =>
                            toggleFlag(
                              idx,
                              "alerts_muted",
                              ev.target.checked,
                              "Messages setting",
                            )
                          }
                        />
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <span className={granted === null ? "subtle" : "tag"}>
                          {pageLabel}
                        </span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="linklike"
                          aria-expanded={open}
                          onClick={() => setExpanded(open ? null : idx)}
                        >
                          {open ? "Hide settings ▴" : "Settings ▸"}
                        </button>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="tag-danger"
                          onClick={() =>
                            setEntries(entries.filter((_, i) => i !== idx))
                          }
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                    {open && (
                      <tr className="staff-settings-row">
                        <td colSpan={7}>
                          <StaffSettingsPanel
                            entry={e}
                            saving={saving}
                            onText={(patch) => update(idx, patch)}
                            onFlag={(field, checked, label) =>
                              toggleFlag(idx, field, checked, label)
                            }
                            onGrid={(gridType, channel, checked, label) =>
                              toggleGridCell(idx, gridType, channel, checked, label)
                            }
                            onPages={(next) => update(idx, { visible_pages: next })}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
          <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem" }}>
            <button
              type="button"
              onClick={() =>
                setEntries([
                  ...entries,
                  {
                    phone: "+44",
                    staff_id: uuidv7(),
                    display_name: "",
                    admin: false,
                    voter: false,
                    // Migration 192 — mirrors DEFAULT_NOTIFICATION_GRID in
                    // packages/canonical-data/src/l2-yaml.ts (the writer
                    // seeds the same for a brand-new person): the
                    // formerly-default-open lanes on WhatsApp, ballots on
                    // both tap rails, everything else off until ticked.
                    notification_grid: {
                      payments: ["whatsapp"],
                      bookings: ["whatsapp"],
                      members: ["whatsapp"],
                      instagram: ["whatsapp"],
                      rota: ["whatsapp"],
                      ratings: ["whatsapp"],
                      digests: ["whatsapp"],
                      infra: ["whatsapp"],
                      decisions: ["whatsapp", "telegram"],
                    },
                  },
                ])
              }
            >
              + Add staff
            </button>
            <button type="button" disabled={saving} onClick={() => void save()}>
              {saving ? "Saving…" : "Save allowlist"}
            </button>
          </div>
        </>
      )}
    </fieldset>
  );
}

// ---------------------------------------------------------------------------
// ChatToolCatalogSection — phase 66d (per-tier tool catalogues).
//
// Three INDEPENDENT allowlists controlling exactly which canonical actions
// each chat tier's LLM receives in Anthropic's `tools` array. Fewer tools =
// a cheaper, sharper prompt — the agent's tool catalogue is core IP:
//
//   • Client · members  — actions admitting `human:member`
//   • Staff · non-admin  — actions admitting `human:staff`
//   • Admin staff        — actions admitting `human:staff`
//
// Each tier writes its own `chat_translator.tool_allowlist_<tier>` field.
// ABSENT ⇒ that tier inherits its fallback (member/staff → legacy shared
// `tool_allowlist`; admin → the FULL staff catalogue / historical bypass).
// Editing a tier materialises an EXPLICIT list of the ticked actions (pinned +
// unambiguous); "Reset to default" drops the field to restore the fallback.
// Mirrors `resolveTierToolAllowlist` in canonical-data — keep in lockstep.
// ---------------------------------------------------------------------------

// Actions hard-excluded from EVERY chat tier by the chat-translator catalog
// filter (`EXCLUDED_TOOL_NAMES` in
// packages/chat-translator/src/catalog-filter.ts). Ticking them would be inert
// (the filter strips them regardless), so they are hidden from all tier
// checklists. Keep in sync with that set.
type ChatTierKey = "member" | "staff" | "admin";

interface ChatTierMeta {
  readonly key: ChatTierKey;
  readonly field:
    | "tool_allowlist_member"
    | "tool_allowlist_staff"
    | "tool_allowlist_admin";
  readonly label: string;
  readonly actorKind: "human:member" | "human:staff";
  readonly blurb: string;
}

const CHAT_TIERS: ReadonlyArray<ChatTierMeta> = [
  {
    key: "member",
    field: "tool_allowlist_member",
    label: "Client · members",
    actorKind: "human:member",
    blurb:
      "Tools the customer-facing chat agent may use on a member's behalf. Default (none set) inherits the shared list.",
  },
  {
    key: "staff",
    field: "tool_allowlist_staff",
    label: "Staff · non-admin",
    actorKind: "human:staff",
    blurb:
      "Tools a normal (non-admin) staff phone's chat agent may use. Default (none set) inherits the shared list.",
  },
  {
    key: "admin",
    field: "tool_allowlist_admin",
    label: "Admin staff",
    actorKind: "human:staff",
    blurb:
      "Tools an admin phone's chat agent may use. Default (none set) = the FULL staff catalogue (the historical admin bypass). Set a list to restrict admin too.",
  },
];

interface TierCatalogAction {
  readonly name: string;
  readonly allowedActors: ReadonlyArray<string>;
}

// 2026-08-07 — the hand-written `CHAT_HARD_EXCLUDED` that used to live here is
// GONE. It was authored at 12 entries while the real set in
// `packages/chat-translator/src/catalog-filter.ts` grew to 27, so 15 actions
// that can never be surfaced to any chat agent — `edit_staff_allowlist`,
// `notify_staff`, `send_gmail`, `post_instagram`, the Stripe intents … —
// rendered here as tickable checkboxes whose ticks did nothing. The flag now
// rides `/api/catalog` as `chat_hard_excluded`; do not re-introduce a copy.
//
// 2026-08-07 (second pass) — that fix hid all 27, which is now only right for
// the 7 in the NEVER tier. The other 20 moved to `CHAT_ADMIN_ONLY_TOOL_NAMES`:
// an admin staff chat DOES get them, no tier can be given them, so they are
// still un-tickable but they are no longer "unavailable". They render below
// the checklists as a read-only list. Hiding them outright would repeat the
// original defect in the other direction — a page that understates what the
// agent can do is as wrong as one that overstates it.

function ChatToolCatalogSection({
  config,
  setConfig,
}: SectionProps): JSX.Element {
  const [actions, setActions] = useState<ReadonlyArray<TierCatalogAction> | null>(
    null,
  );
  const [adminOnly, setAdminOnly] = useState<ReadonlyArray<string>>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .catalog()
      .then((cat) => {
        if (cancelled) return;
        const entries: TierCatalogAction[] = cat.actions
          .filter((a) => a.chat_hard_excluded !== true)
          .map((a) => ({ name: a.name, allowedActors: a.allowed_actors }));
        entries.sort((x, y) => x.name.localeCompare(y.name));
        setActions(entries);
        setAdminOnly(
          cat.actions
            .filter((a) => a.chat_admin_only === true)
            .map((a) => a.name)
            .sort((x, y) => x.localeCompare(y)),
        );
      })
      .catch((e: Error) => {
        if (!cancelled) setLoadError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <fieldset>
      <legend>Chat agent actions</legend>
      <p className="subtle" style={{ marginTop: 0 }}>
        Control exactly which canonical actions each chat tier's agent receives
        in its tool catalogue. Fewer tools = a cheaper, sharper prompt. Unticked
        = the agent never sees it (blocked at tool-list time). Each tier is
        independent; an unedited tier inherits its default, shown per tier.
        Newly-added canonical actions appear automatically for any tier left on
        its default — a tier you have explicitly edited is pinned to its list.
      </p>
      {loadError !== null && (
        <p className="tag tag-danger">
          Failed to load action catalog: {loadError}
        </p>
      )}
      {actions === null && loadError === null && (
        <p className="subtle">Loading…</p>
      )}
      {actions !== null &&
        CHAT_TIERS.map((tier) => (
          <TierToolChecklist
            key={tier.key}
            tier={tier}
            actions={actions}
            config={config}
            setConfig={setConfig}
          />
        ))}
      {adminOnly.length > 0 && (
        <div style={{ marginTop: "1.25rem" }}>
          <strong>Admin staff always get these ({adminOnly.length})</strong>
          <p className="subtle" style={{ marginTop: ".25rem" }}>
            A staff number ticked as <em>Admin</em> in the Staff allowlist above
            receives these automatically, and no tier can be given them by
            ticking — which is why they are listed rather than offered as
            checkboxes. Several are irreversible; the agent must summarise and
            wait for <code>PROCEED</code> before calling one. To take them away
            from someone, untick their Admin box — to stop messaging them
            without touching their rights, use <em>don&apos;t send messages</em>.
          </p>
          <p className="subtle" style={{ marginTop: ".25rem" }}>
            {adminOnly.map((n, i) => (
              <span key={n}>
                {i > 0 ? ", " : ""}
                <code>{n}</code>
              </span>
            ))}
          </p>
        </div>
      )}
    </fieldset>
  );
}

function TierToolChecklist({
  tier,
  actions,
  config,
  setConfig,
}: {
  tier: ChatTierMeta;
  actions: ReadonlyArray<TierCatalogAction>;
  config: StudioL2ConfigShape;
  setConfig: (c: StudioL2ConfigShape) => void;
}): JSX.Element {
  const ct = config.chat_translator ?? {};
  const explicit = ct[tier.field];
  // The legacy shared list is the member/staff fallback ONLY (never admin).
  const legacy = tier.key === "admin" ? undefined : ct.tool_allowlist;
  const isDefault = explicit === undefined;

  // Actions eligible for this tier's actor kind — the only ones the tier can
  // actually dispatch. We show eligible actions only (a non-eligible tick is
  // inert at the catalog filter).
  const eligible = actions.filter((a) =>
    a.allowedActors.includes(tier.actorKind),
  );
  const eligibleNames = eligible.map((a) => a.name);

  // "On" derivation per eligible action:
  //   explicit present → membership in the explicit list
  //   else legacy present → membership in the legacy shared list
  //   else → on (default full)
  const isOn = (name: string): boolean => {
    if (explicit !== undefined) return explicit.includes(name);
    if (legacy !== undefined) return legacy.includes(name);
    return true;
  };
  const checkedEligible = new Set(eligibleNames.filter(isOn));

  const writeExplicit = (names: ReadonlySet<string>) => {
    setConfig({
      ...config,
      chat_translator: setOrDropAny(ct, tier.field, Array.from(names).sort()),
    });
  };
  const toggle = (name: string) => {
    const next = new Set(checkedEligible);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    writeExplicit(next);
  };
  const setAll = (on: boolean) => {
    writeExplicit(on ? new Set(eligibleNames) : new Set<string>());
  };
  const resetDefault = () => {
    setConfig({
      ...config,
      chat_translator: setOrDropAny(ct, tier.field, undefined),
    });
  };

  const defaultLabel =
    tier.key === "admin"
      ? "full staff catalogue"
      : legacy !== undefined
        ? "shared list"
        : "all tools";

  return (
    <div
      style={{
        border: "1px solid #e2e2e2",
        borderRadius: 6,
        padding: "10px 12px",
        marginBottom: 10,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <strong>{tier.label}</strong>
        <span
          className="tag"
          style={{
            background: isDefault ? "#C9CFF3" : "#f7e9c6",
            color: isDefault ? "#14278F" : "#7a4e00",
            fontSize: "0.75em",
          }}
        >
          {isDefault
            ? `default · ${defaultLabel}`
            : `custom · ${(explicit ?? []).length} tools`}
        </span>
      </div>
      <p className="subtle" style={{ margin: "4px 0 8px" }}>
        {tier.blurb}
      </p>
      <div
        style={{
          display: "flex",
          gap: 8,
          marginBottom: 8,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <button type="button" onClick={() => setAll(true)}>
          Check all
        </button>
        <button type="button" onClick={() => setAll(false)}>
          Uncheck all
        </button>
        <button type="button" onClick={resetDefault} disabled={isDefault}>
          Reset to default
        </button>
        <span className="subtle">
          {checkedEligible.size}/{eligibleNames.length} enabled
        </span>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
          gap: "4px 12px",
        }}
      >
        {eligible.map((a) => (
          <label
            key={a.name}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontFamily: "monospace",
              fontSize: "0.9em",
            }}
          >
            <input
              type="checkbox"
              checked={checkedEligible.has(a.name)}
              onChange={() => toggle(a.name)}
            />
            {a.name}
          </label>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PersonalTrainersSection — phase 41 follow-up.
//
// Trainers are NOT in L2 YAML — they're rows in the canonical
// `trainers` Postgres table (per-studio, scoped by RLS). This section
// reads the existing trainers via /api/data/trainers and lets the
// operator add new ones inline by dispatching `staff_create_trainer`
// through the chokepoint. Edit + delete are deferred to /data/trainers
// (the existing row-edit modal); a "Manage trainers →" link points
// there.
//
// `props` is intentionally empty — the section doesn't touch the
// surrounding YAML config / setConfig. The reason it lives in the
// /config editor rather than its own page: trainers are part of
// "studio setup" conceptually, even if the storage substrate differs.
// ---------------------------------------------------------------------------

interface TrainerRow {
  readonly id: string;
  readonly display_name: string;
  readonly email: string | null;
  readonly status: string;
  readonly phone?: string | null;
  // Per-trainer post-session capture switch (default ON). Absent on older API
  // shapes ⇒ treated as ON.
  readonly send_post_session_message?: boolean;
  /**
   * Migration 179 — the other five proactive trainer messages. Same
   * absent-reads-as-ON convention as the switch above, so a row from an older
   * API shape is never rendered as opted out.
   */
  readonly send_morning_brief?: boolean;
  readonly send_task_digest?: boolean;
  readonly send_rota_requests?: boolean;
  readonly send_payroll_statements?: boolean;
  readonly send_polls?: boolean;
}

function isTrainerRow(row: Record<string, unknown>): row is TrainerRow & Record<string, unknown> {
  return (
    typeof row["id"] === "string" &&
    typeof row["display_name"] === "string" &&
    (row["email"] === null || typeof row["email"] === "string") &&
    typeof row["status"] === "string"
  );
}

/**
 * The six proactive messages a trainer can be opted out of, in the order an
 * operator thinks about them: daily first, then monthly, then ad hoc.
 *
 * Enumerated ONCE so the panel below cannot render a switch the action does
 * not accept, or quietly omit one it does. Each carries the sentence that says
 * what stops — a two-word label like "Rota" would leave the operator guessing
 * whether it also silences the reminders (it does).
 */
const TRAINER_MESSAGE_SWITCHES = [
  {
    field: "send_morning_brief",
    label: "Morning brief",
    when: "06:00 daily · WhatsApp",
    what: "Their day's clients, with safety flags, credits and last-known context.",
  },
  {
    field: "send_task_digest",
    label: "Task digest",
    when: "07:00 daily · WhatsApp",
    what: "Their open tasks, soonest-due first. Only sent on days they have one.",
  },
  {
    field: "send_post_session_message",
    label: "Post-session prompt",
    when: "after each session · WhatsApp",
    what: "Asks them to fill a gap in the member's profile. Also needs the studio-wide /flows toggle and the member's own switch.",
  },
  {
    field: "send_rota_requests",
    label: "Rota ask + reminders",
    when: "monthly, 20th/24th/27th · Telegram",
    what: "Asks for next month's availability, then chases twice. Off means they are never asked — take their rota another way.",
  },
  {
    field: "send_payroll_statements",
    label: "Payroll statement",
    when: "monthly, after your tap · Telegram",
    what: "Their pay for the month. Off changes NOTHING about what they are paid — only whether they are messaged about it.",
  },
  {
    field: "send_polls",
    label: "Poll invitations",
    when: "ad hoc · WhatsApp",
    what: "Off means they are not enrolled in new polls at all, so a poll never waits on a reply they were not asked for.",
  },
] as const;

type TrainerSwitchField = (typeof TRAINER_MESSAGE_SWITCHES)[number]["field"];

/**
 * One per-trainer message switch (all default ON). Persists the named column
 * via the `update_trainer` action, then asks the parent to reload.
 *
 * Generalised from the single post-session checkbox on 2026-08-18 (migration
 * 179). `update_trainer` takes a PATCH, so one field at a time is exactly
 * right here — unlike the staff allowlist's full-replace, none of the
 * omitted-key hazards apply and a toggle can safely send only what changed.
 */
function TrainerSendToggle({
  trainer,
  field,
  onDone,
}: {
  readonly trainer: TrainerRow;
  readonly field: TrainerSwitchField;
  readonly onDone: () => void;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Absent reads as ON — an older API shape must never render as opted out,
  // which would invite an operator to "fix" a switch that was never off.
  const current = trainer[field] !== false;

  const set = async (next: boolean): Promise<void> => {
    if (busy || next === current) return;
    setBusy(true);
    setError(null);
    try {
      const r = await dispatchAction<
        { trainer_id: string; patch: Record<string, boolean> },
        { updated: true }
      >("update_trainer", {
        trainer_id: trainer.id,
        // ONE key: `update_trainer` is a patch, so sending only what changed
        // is both correct and the smallest possible blast radius.
        patch: { [field]: next },
      });
      if (!r.ok) setError(`${r.code}`);
      else onDone();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <label>
      <input
        type="checkbox"
        checked={current}
        disabled={busy}
        onChange={(e) => void set(e.target.checked)}
      />
      {error !== null ? (
        <span className="tag tag-danger" style={{ marginLeft: 6 }}>
          {error}
        </span>
      ) : null}
    </label>
  );
}

/**
 * "Add trainer" — the pane-head trigger next to "Save all changes"
 * (2026-08-19 trainer-roster-unification follow-up). Opens the full-profile
 * intake modal over `staff_create_trainer`: ONE write that then flows to
 * every derived surface — the booking dropdown, the website Team page
 * (runtime fetch), the chat agent's KB "trainers" topic (auto-synced by the
 * action itself), and the onboarding suggestion matcher. Replaces the old
 * two-field inline row at the bottom of the trainers card, which could only
 * set a name + email and left the profile to /data/trainers edits.
 */
function AddTrainerButton({
  onCreated,
}: {
  readonly onCreated: () => void;
}): JSX.Element {
  return (
    <ActionModalButton
      action="staff_create_trainer"
      buttonLabel="+ Add trainer"
      buttonClassName="config-console-reset"
      buttonTitle="Create a trainer with their full public profile — website, booking dropdown, chat agent and KB all follow this one record."
      modalTitle="Add a trainer"
      description="One record drives every surface: the booking dropdown and website Team page update immediately, and the chat agent's knowledge base re-derives itself from this profile. Bookable times still need availability (rota / Codex) afterwards."
      submitLabel="Create trainer"
      fields={[
        {
          kind: "text",
          name: "email",
          label: "Email",
          required: true,
          placeholder: "anna@e-pulsive.co.uk",
        },
        {
          kind: "text",
          name: "display_name",
          label: "Name",
          required: true,
          placeholder: "e.g. Anna",
          maxLength: 200,
        },
        {
          kind: "text",
          name: "phone",
          label: "Phone",
          placeholder: "+447700900000 (E.164) — morning brief + trainer messages",
        },
        {
          kind: "text",
          name: "title",
          label: "Title / heading",
          placeholder: "e.g. EMS & Mental Health Specialist",
          maxLength: 120,
        },
        {
          kind: "textarea",
          name: "bio",
          label: "Bio",
          placeholder:
            "Shown on the website Team page and used by the chat agent when members ask about this trainer.",
          maxLength: 2000,
        },
        {
          kind: "select",
          name: "gender",
          label: "Gender",
          options: [
            { value: "", label: "—" },
            { value: "Male", label: "Male" },
            { value: "Female", label: "Female" },
          ],
        },
        {
          kind: "text",
          name: "activities",
          label: "Activities (comma-separated)",
          placeholder: "e.g. Running, Football — drives onboarding suggestions",
        },
        {
          kind: "textarea",
          name: "media",
          label: "Photos / videos (one https URL per line)",
          placeholder:
            "https://assets.e-pulsive.co.uk/anna.webp\nUpload to the assets library first; .mp4 lines render as video.",
        },
        {
          kind: "text",
          name: "work_email",
          label: "Work email",
          placeholder: "anna@e-pulsive.co.uk (if different from login email)",
        },
        {
          kind: "number",
          name: "sort_order",
          label: "Website position",
          min: 0,
          max: 1000,
          placeholder: "1 = first on the Team page; empty = last",
        },
      ]}
      validate={(values) => {
        const media = typeof values["media"] === "string" ? values["media"] : "";
        const bad = media
          .split("\n")
          .map((l) => l.trim())
          .filter((l) => l.length > 0)
          .find((l) => !l.startsWith("https://"));
        return bad !== undefined
          ? `Media URLs must be absolute https:// links (got "${bad}"). Upload the file to the assets library first.`
          : null;
      }}
      transformInput={(input) => {
        const out: Record<string, unknown> = { ...input };
        if (typeof out["activities"] === "string") {
          const list = (out["activities"] as string)
            .split(",")
            .map((s) => s.trim())
            .filter((s) => s.length > 0);
          if (list.length > 0) out["activities"] = list;
          else delete out["activities"];
        }
        if (typeof out["media"] === "string") {
          const list = (out["media"] as string)
            .split("\n")
            .map((s) => s.trim())
            .filter((s) => s.length > 0);
          if (list.length > 0) out["media"] = list;
          else delete out["media"];
        }
        return out;
      }}
      onDone={onCreated}
    />
  );
}

// `studio.trainer_comms_admin_mode` — a YAML-backed studio setting with three
// mutually exclusive values, which is why it is radios and not two checkboxes
// (two booleans would allow a fourth, meaningless state). It governs the T1
// morning brief + T3 post-session prompt ONLY — messages sent TO trainers,
// which is why the box lives in the Trainers pane (it sat inside Staff
// allowlist until 2026-08-29, where "why isn't Juliana getting her brief?"
// never thought to look). Edits update the shared `config` and persist with
// the page "Save all changes" button (NOT the trainers table's instant
// dispatch path). The admin phone hint is read from the staff allowlist via
// its OWN fetch: panes mount one at a time, so the allowlist section's copy
// is unmounted while this pane is open.
function TrainerCommsRoutingBox({
  slug,
  config,
  setConfig,
}: {
  readonly slug?: string | undefined;
  readonly config: StudioL2ConfigShape;
  readonly setConfig: (c: StudioL2ConfigShape) => void;
}): JSX.Element {
  const [adminPhones, setAdminPhones] = useState<ReadonlyArray<string>>([]);
  const [phonesLoaded, setPhonesLoaded] = useState(false);
  useEffect(() => {
    if (slug === undefined) return;
    let cancelled = false;
    api
      .getStaffAllowlist(slug)
      .then((resp) => {
        if (cancelled) return;
        setAdminPhones(
          resp.entries
            .filter((e) => e.admin === true && e.phone.trim() !== "")
            .map((e) => e.phone.trim()),
        );
        setPhonesLoaded(true);
      })
      .catch(() => {
        // The phone hint is informational — a failed allowlist read renders
        // the box without numbers rather than blocking the radios.
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);
  const trainerCommsMode =
    config.studio?.trainer_comms_admin_mode ?? ("off" as const);
  const setTrainerCommsMode = (mode: "off" | "redirect" | "copy"): void => {
    setConfig({
      ...config,
      studio: setOrDropAny(
        config.studio ?? {},
        "trainer_comms_admin_mode",
        // `off` is the default, so it is DROPPED rather than written — the
        // config stays free of a key that only restates the default.
        mode === "off" ? undefined : mode,
      ),
    });
  };
  return (
    <div
      style={{
        border: "1px solid var(--border, #ccc)",
        borderRadius: 6,
        padding: "0.6rem 0.75rem",
        margin: "0.5rem 0 0.75rem",
      }}
    >
      <strong>Trainer comms — who receives them</strong>
      <p className="subtle" style={{ margin: "0.3rem 0 0.5rem" }}>
        Applies to the <strong>morning brief</strong> and the{" "}
        <strong>post-session prompt</strong> — those two only. Rota asks,
        payroll statements, the task digest and polls are not affected by
        this setting. Copies go to the <strong>admin</strong> number(s) from
        the Staff allowlist
        {adminPhones.length > 0 ? (
          <>
            {" "}
            (<code>{adminPhones.join(", ")}</code>)
          </>
        ) : phonesLoaded ? (
          <>
            {" "}
            (no admin phone set yet — tick an admin row in Staff allowlist and
            add a phone)
          </>
        ) : null}
        . Saves with <strong>&ldquo;Save all changes&rdquo;</strong> at the
        top of the page (this is a studio config field, not a trainer record).
      </p>
      {(
        [
          {
            value: "off" as const,
            label: "Trainer only",
            help: "Normal operation. Each trainer's own number, nobody copied.",
          },
          {
            value: "copy" as const,
            label: "Trainer and admin",
            help: "The trainer gets it as usual AND the admin number(s) get a copy. A trainer with no phone on file is skipped entirely — there is no send to copy, so the admin gets nothing for them either.",
          },
          {
            value: "redirect" as const,
            label: "Admin only (redirect)",
            help: "The admin number(s) INSTEAD of the trainer — no trainer is messaged at all. Includes trainers with no phone on file, so you can preview everyone. Use while onboarding trainer numbers; switch away at go-live.",
          },
        ] as const
      ).map((opt) => (
        <label
          key={opt.value}
          style={{
            display: "flex",
            gap: "0.5rem",
            alignItems: "flex-start",
            margin: "0.35rem 0",
          }}
        >
          <input
            type="radio"
            name="trainer_comms_admin_mode"
            value={opt.value}
            checked={trainerCommsMode === opt.value}
            onChange={() => setTrainerCommsMode(opt.value)}
            style={{ marginTop: "0.25rem" }}
          />
          <span>
            <strong>{opt.label}</strong>
            <span className="subtle"> — {opt.help}</span>
          </span>
        </label>
      ))}
      {phonesLoaded &&
        trainerCommsMode !== "off" &&
        adminPhones.length === 0 && (
          <p className="subtle" style={{ margin: "0.4rem 0 0" }}>
            <strong>No admin phone is set</strong>, so this has no effect:
            trainers keep receiving their own messages exactly as under
            &ldquo;Trainer only&rdquo;. Tick an admin row in the Staff
            allowlist section and give it a phone number.
          </p>
        )}
    </div>
  );
}

function PersonalTrainersSection({
  slug,
  config,
  setConfig,
  refreshKey = 0,
}: {
  readonly slug?: string | undefined;
  readonly config: StudioL2ConfigShape;
  readonly setConfig: (c: StudioL2ConfigShape) => void;
  readonly refreshKey?: number;
}): JSX.Element {
  const [trainers, setTrainers] = useState<ReadonlyArray<TrainerRow>>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  // Which trainer's message panel is open, by id (NOT index — the list
  // reloads after every toggle, and an index would follow the wrong row if the
  // ordering ever changed under it).
  const [expandedTrainer, setExpandedTrainer] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setListError(null);
    try {
      const resp = await api.data("trainers", null, 100);
      const rows: TrainerRow[] = [];
      for (const r of resp.rows) {
        // Soft-deleted trainers (status='deleted') stay in the canonical
        // table for referential integrity but are hidden from the editor.
        if (isTrainerRow(r) && r.status !== "deleted") rows.push(r);
      }
      setTrainers(rows);
    } catch (e) {
      if (e instanceof ApiError) {
        setListError(`api_error:${e.status}${e.code ? `:${e.code}` : ""}`);
      } else {
        setListError((e as Error).message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // `refreshKey` bumps when the pane-head "Add trainer" modal creates a row
  // (the modal lives beside "Save all changes", outside this section).
  useEffect(() => {
    void reload();
  }, [reload, refreshKey]);

  const handleRemove = useCallback(
    async (trainer: TrainerRow) => {
      const ok = window.confirm(
        `Remove trainer "${trainer.display_name}"? They'll be hidden from the studio. Past bookings and slots are preserved.`,
      );
      if (!ok) return;
      setRemovingId(trainer.id);
      setRemoveError(null);
      try {
        const result = await dispatchAction<
          { trainer_id: string },
          { deleted: true; trainer_id: string }
        >("delete_trainer", { trainer_id: trainer.id });
        if (!result.ok) {
          setRemoveError(`${result.code}: ${result.message}`);
        } else {
          void reload();
        }
      } catch (e) {
        setRemoveError((e as Error).message);
      } finally {
        setRemovingId(null);
      }
    },
    [reload],
  );

  return (
    <fieldset>
      <legend>Personal trainers · staff who deliver sessions</legend>
      <p className="hint">
        Stored in the canonical <code>trainers</code> table (not L2 YAML).
        The <strong>+ Add trainer</strong> button beside “Save all changes”
        opens the full-profile intake (title, bio, photos, website position —
        the record the website Team page, booking dropdown and chat agent all
        derive from). Remove hides a trainer (soft-delete — past bookings and
        slots are preserved). Edits live on{" "}
        <Link to="/data/trainers">/data/trainers</Link>.
      </p>
      <TrainerCommsRoutingBox slug={slug} config={config} setConfig={setConfig} />
      {listError !== null && (
        <div className="tag tag-danger">Failed to load: {listError}</div>
      )}
      {loading && <p className="subtle">Loading…</p>}
      {!loading && listError === null && trainers.length === 0 && (
        <p className="subtle">
          No trainers yet. Use <strong>+ Add trainer</strong> beside “Save all
          changes”, or run{" "}
          <code>pnpm cli action dispatch staff_create_trainer …</code>.
        </p>
      )}
      {!loading && trainers.length > 0 && (
        <table className="config-table staff-roster">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Status</th>
              <th title="How many of the six proactive messages this trainer receives.">
                messages
              </th>
              <th></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {trainers.map((t) => {
              const open = expandedTrainer === t.id;
              const on = TRAINER_MESSAGE_SWITCHES.filter(
                (sw) => t[sw.field] !== false,
              ).length;
              return (
                <Fragment key={t.id}>
                  <tr>
                    <td>{t.display_name}</td>
                    <td>{t.email ?? "—"}</td>
                    <td>{t.phone ?? "—"}</td>
                    <td>{t.status}</td>
                    <td style={{ textAlign: "center" }}>
                      <span
                        className={
                          on === TRAINER_MESSAGE_SWITCHES.length ? "subtle" : "tag"
                        }
                      >
                        {on}/{TRAINER_MESSAGE_SWITCHES.length}
                      </span>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="linklike"
                        aria-expanded={open}
                        onClick={() => setExpandedTrainer(open ? null : t.id)}
                      >
                        {open ? "Hide messages ▴" : "Messages ▸"}
                      </button>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="tag-danger"
                        onClick={() => void handleRemove(t)}
                        disabled={removingId !== null}
                      >
                        {removingId === t.id ? "Removing…" : "Remove"}
                      </button>
                    </td>
                  </tr>
                  {open && (
                    <tr className="staff-settings-row">
                      <td colSpan={7}>
                        <div className="staff-settings">
                          <fieldset className="staff-settings-block">
                            <legend>
                              Messages {t.display_name} receives
                            </legend>
                            <p className="subtle">
                              All default ON. Unticking one stops that message
                              reaching them and takes effect on the next send —
                              each saves instantly. Two things stay on
                              whatever you do here: replies to messages they
                              send the rota bot, and the confirmation when they
                              first connect it. Answering someone who wrote to
                              you is not a notification.
                            </p>
                            <div className="trainer-messages">
                              {TRAINER_MESSAGE_SWITCHES.map((sw) => (
                                <div key={sw.field} className="trainer-message">
                                  <TrainerSendToggle
                                    trainer={t}
                                    field={sw.field}
                                    onDone={() => void reload()}
                                  />
                                  <div>
                                    <div className="trainer-message-head">
                                      {sw.label}{" "}
                                      <span className="subtle">· {sw.when}</span>
                                    </div>
                                    <div className="subtle">{sw.what}</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </fieldset>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      )}
      {removeError !== null && (
        <div className="tag tag-danger">Remove failed: {removeError}</div>
      )}
    </fieldset>
  );
}

// ---------------------------------------------------------------------------
// TrainerPayrollSection — 2026-08-04 (trainer-ops lane B follow-up).
//
// Edits the L2 `payroll:` block: studio-wide default pay terms plus
// per-trainer overrides keyed by trainer UUID, joined to display names via
// /api/data/trainers. Semantics mirror the engine's resolver
// (`resolveTrainerCompensation`): a trainer with NO row here inherits the
// defaults wholesale; a row's blank rate fields inherit field-by-field; the
// row's pay model always wins. Rides the page-level full-replace Save — the
// block is override-covered, so a Save is live at once and the NEXT payroll
// run/recompute freezes it into that run's config snapshot (approved months
// never move).
// ---------------------------------------------------------------------------

/** "" ⇔ null round-trip for £ inputs (stored as integer minor units). */
function minorToPounds(m: number | null | undefined): string {
  return m === null || m === undefined ? "" : String(m / 100);
}
function poundsToMinor(s: string): number | null {
  const t = s.trim();
  if (t === "") return null;
  const n = Number(t);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}
function pctFromInput(s: string): number | null {
  const t = s.trim();
  if (t === "") return null;
  const n = Number(t);
  if (!Number.isFinite(n) || n < 0 || n > 100) return null;
  return n;
}

const EMPTY_COMP: PayrollCompShape = {
  pay_model: "revenue_share",
  hourly_rate_minor: null,
  revenue_share_pct: null,
  pr_session_rate_minor: null,
};

function TrainerPayrollSection({ config, setConfig }: SectionProps): JSX.Element {
  const [trainers, setTrainers] = useState<ReadonlyArray<TrainerRow>>([]);
  const [listError, setListError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const resp = await api.data("trainers", null, 100);
        if (cancelled) return;
        const rows: TrainerRow[] = [];
        for (const r of resp.rows) {
          if (isTrainerRow(r) && r.status !== "deleted") rows.push(r);
        }
        setTrainers(rows);
      } catch (e) {
        if (!cancelled) setListError((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const payroll = config.payroll;
  const patch = (p: PayrollBlockShape): void =>
    setConfig({ ...config, payroll: p });

  if (payroll === undefined) {
    return (
      <fieldset>
        <legend>Trainer payroll · pay models, rates &amp; bonus</legend>
        <p className="hint">
          This studio has no <code>payroll:</code> block yet, so the payroll
          engine refuses to run for it. Seed one below (defaults you can edit
          before saving), then press Save.
        </p>
        <button
          type="button"
          onClick={() =>
            patch({
              effective_from: new Date().toISOString().slice(0, 10),
              pay_no_shows: true,
              bonus: { new_signup_pct: 7.5, renewal_pct: 5 },
              defaults: {
                pay_model: "revenue_share",
                revenue_share_pct: 25,
                hourly_rate_minor: 2000,
                pr_session_rate_minor: 1500,
              },
              trainers: {},
            })
          }
        >
          + Seed payroll config
        </button>
      </fieldset>
    );
  }

  const defaults = payroll.defaults ?? EMPTY_COMP;
  const entries = payroll.trainers ?? {};
  const patchDefaults = (d: PayrollCompShape): void =>
    patch({ ...payroll, defaults: d });
  const patchEntry = (trainerId: string, comp: PayrollCompShape | null): void => {
    const next: Record<string, PayrollCompShape> = { ...entries };
    if (comp === null) delete next[trainerId];
    else next[trainerId] = comp;
    patch({ ...payroll, trainers: next });
  };

  // Rows: every live trainer, PLUS any stale override entry whose UUID no
  // longer matches a live trainer (rendered so it can't linger invisibly).
  const liveIds = new Set(trainers.map((t) => t.id));
  const staleIds = Object.keys(entries).filter((id) => !liveIds.has(id));

  const compRow = (
    label: JSX.Element | string,
    trainerId: string,
    entry: PayrollCompShape | undefined,
  ): JSX.Element => {
    const custom = entry !== undefined;
    const comp = entry ?? EMPTY_COMP;
    return (
      <tr key={trainerId}>
        <td>{label}</td>
        <td>
          <select
            value={custom ? comp.pay_model : "__default__"}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "__default__") patchEntry(trainerId, null);
              else
                patchEntry(trainerId, {
                  ...comp,
                  pay_model: v as PayrollCompShape["pay_model"],
                });
            }}
          >
            <option value="__default__">Studio default</option>
            <option value="revenue_share">Revenue share</option>
            <option value="hourly">Hourly</option>
          </select>
        </td>
        <td>
          <input
            type="number"
            min={0}
            max={100}
            step={0.5}
            style={{ width: "5.5em" }}
            disabled={!custom}
            value={custom ? (comp.revenue_share_pct ?? "") : ""}
            placeholder={String(defaults.revenue_share_pct ?? "")}
            title="Blank inherits the studio default"
            onChange={(e) =>
              patchEntry(trainerId, {
                ...comp,
                revenue_share_pct: pctFromInput(e.target.value),
              })
            }
          />
        </td>
        <td>
          <input
            type="number"
            min={0}
            step={0.5}
            style={{ width: "5.5em" }}
            disabled={!custom}
            value={custom ? minorToPounds(comp.hourly_rate_minor) : ""}
            placeholder={minorToPounds(defaults.hourly_rate_minor)}
            title="Blank inherits the studio default"
            onChange={(e) =>
              patchEntry(trainerId, {
                ...comp,
                hourly_rate_minor: poundsToMinor(e.target.value),
              })
            }
          />
        </td>
        <td>
          <input
            type="number"
            min={0}
            step={0.5}
            style={{ width: "5.5em" }}
            disabled={!custom}
            value={custom ? minorToPounds(comp.pr_session_rate_minor) : ""}
            placeholder={minorToPounds(defaults.pr_session_rate_minor)}
            title="Blank inherits the studio default"
            onChange={(e) =>
              patchEntry(trainerId, {
                ...comp,
                pr_session_rate_minor: poundsToMinor(e.target.value),
              })
            }
          />
        </td>
      </tr>
    );
  };

  return (
    <fieldset>
      <legend>Trainer payroll · pay models, rates &amp; bonus</legend>
      <p className="hint">
        Per-trainer terms override the studio defaults; &quot;Studio
        default&quot; rows and blank rate fields inherit them. Changes apply to
        the NEXT payroll run or recompute — approved months keep their frozen
        snapshot. Amounts are in pounds.
      </p>

      <h4>Studio defaults</h4>
      <div className="form-row">
        <label>Default pay model</label>
        <select
          value={defaults.pay_model}
          onChange={(e) =>
            patchDefaults({
              ...defaults,
              pay_model: e.target.value as PayrollCompShape["pay_model"],
            })
          }
        >
          <option value="revenue_share">Revenue share</option>
          <option value="hourly">Hourly</option>
        </select>
      </div>
      <div className="form-row">
        <label>Revenue share (% of session revenue)</label>
        <input
          type="number"
          min={0}
          max={100}
          step={0.5}
          value={defaults.revenue_share_pct ?? ""}
          onChange={(e) =>
            patchDefaults({
              ...defaults,
              revenue_share_pct: pctFromInput(e.target.value),
            })
          }
        />
      </div>
      <div className="form-row">
        <label>Hourly rate (£/hour)</label>
        <input
          type="number"
          min={0}
          step={0.5}
          value={minorToPounds(defaults.hourly_rate_minor)}
          onChange={(e) =>
            patchDefaults({
              ...defaults,
              hourly_rate_minor: poundsToMinor(e.target.value),
            })
          }
        />
      </div>
      <div className="form-row">
        <label>PR session rate (£/session, journalists &amp; influencers)</label>
        <input
          type="number"
          min={0}
          step={0.5}
          value={minorToPounds(defaults.pr_session_rate_minor)}
          onChange={(e) =>
            patchDefaults({
              ...defaults,
              pr_session_rate_minor: poundsToMinor(e.target.value),
            })
          }
        />
      </div>

      <h4>Bonus scheme</h4>
      <div className="form-row">
        <label>New sign-up bonus (% of pack / first cycle)</label>
        <input
          type="number"
          min={0}
          max={100}
          step={0.5}
          value={payroll.bonus.new_signup_pct}
          onChange={(e) => {
            const v = pctFromInput(e.target.value);
            if (v !== null)
              patch({ ...payroll, bonus: { ...payroll.bonus, new_signup_pct: v } });
          }}
        />
      </div>
      <div className="form-row">
        <label>Renewal bonus (%)</label>
        <input
          type="number"
          min={0}
          max={100}
          step={0.5}
          value={payroll.bonus.renewal_pct}
          onChange={(e) => {
            const v = pctFromInput(e.target.value);
            if (v !== null)
              patch({ ...payroll, bonus: { ...payroll.bonus, renewal_pct: v } });
          }}
        />
      </div>
      <div className="form-row">
        <label>
          <input
            type="checkbox"
            checked={payroll.pay_no_shows}
            onChange={(e) =>
              patch({ ...payroll, pay_no_shows: e.target.checked })
            }
          />{" "}
          Pay no-shows (the member&apos;s credit was consumed; the trainer held
          the time)
        </label>
      </div>
      <div className="form-row">
        <label>Bonus effective from</label>
        <input
          type="date"
          value={payroll.effective_from}
          onChange={(e) => {
            if (e.target.value !== "")
              patch({ ...payroll, effective_from: e.target.value });
          }}
        />
      </div>

      <h4>Per-trainer overrides</h4>
      {listError !== null && (
        <div className="tag tag-danger">Failed to load trainers: {listError}</div>
      )}
      <table>
        <thead>
          <tr>
            <th>Trainer</th>
            <th>Pay terms</th>
            <th>Share %</th>
            <th>Hourly £</th>
            <th>PR £</th>
          </tr>
        </thead>
        <tbody>
          {trainers.map((t) => compRow(t.display_name, t.id, entries[t.id]))}
          {staleIds.map((id) =>
            compRow(
              <span title={id}>
                <code>{id.slice(0, 8)}…</code>{" "}
                <span className="subtle">(no matching trainer)</span>
              </span>,
              id,
              entries[id],
            ),
          )}
        </tbody>
      </table>
    </fieldset>
  );
}

function MarketingFlowsSection({
  config,
  setConfig,
}: SectionProps): JSX.Element {
  const mf = config.marketing_flows ?? {};
  return (
    <fieldset>
      <legend>Marketing flows · per-studio toggles</legend>
      <p className="subtle" style={{ margin: "0 0 0.75rem" }}>
        The member post-session feedback request (1–5) is now the{" "}
        <strong>Session feedback request</strong> transactional flow — switch it
        on/off on the <code>/flows</code> page. A member still needs their own{" "}
        <strong>Session feedback</strong> toggle On (default Off) to receive it.
      </p>
      <div className="form-row">
        <label>Opt-out URL template (default: https://app.hercie.ai/unsubscribe)</label>
        <input
          type="text"
          value={mf.opt_out_url_template ?? ""}
          placeholder="https://app.hercie.ai/unsubscribe"
          onChange={(e) =>
            setConfig({
              ...config,
              marketing_flows: setOrDrop(
                mf,
                "opt_out_url_template",
                e.target.value,
              ),
            })
          }
        />
      </div>
      <div className="form-row">
        <label>Max active flows per member (range 1..50, default 5)</label>
        <input
          type="number"
          min={1}
          max={50}
          value={mf.max_active_flows_per_member ?? ""}
          placeholder="5"
          onChange={(e) =>
            setConfig({
              ...config,
              marketing_flows: setOrDropAny(
                mf,
                "max_active_flows_per_member",
                e.target.value ? Number(e.target.value) : undefined,
              ),
            })
          }
        />
      </div>
      <p className="hint">
        Per-flow editing happens at <code>/flows/:flow_name/edit</code> (phase 33a
        editor). This section only sets per-studio toggles.
      </p>
    </fieldset>
  );
}

function BookingSystemSection({
  config,
  setConfig,
}: SectionProps): JSX.Element {
  return (
    <fieldset>
      <legend>Booking system (advanced)</legend>
      <p className="hint">
        At MVP only <code>layer-canonical</code> is admitted. Future connector
        phases (Hercie / Mindbody / Klaviyo-bridge / etc) widen the enum.
      </p>
      <div className="form-row">
        <label>Kind</label>
        <select
          value={config.booking_system.kind}
          onChange={(e) =>
            setConfig({
              ...config,
              booking_system: {
                ...config.booking_system,
                kind: e.target.value,
              },
            })
          }
        >
          <option value="layer-canonical">layer-canonical</option>
        </select>
      </div>
      <div className="form-row">
        <label>Availability model</label>
        <select
          value={config.booking_system.availability_model ?? "ongoing"}
          onChange={(e) =>
            setConfig({
              ...config,
              booking_system: {
                ...config.booking_system,
                availability_model:
                  e.target.value === "dated" ? "dated" : "ongoing",
              },
            })
          }
        >
          <option value="dated">
            Dated — rotas are uploaded for explicit dates; no ongoing schedule
          </option>
          <option value="ongoing">
            Ongoing — permanent weekly windows, extended nightly by the roller
          </option>
        </select>
        <p className="hint">
          Dated: an availability paste with no period is refused, Save creates
          every slot for the named dates at once, and the nightly availability
          roller skips this studio. Live on Save.
        </p>
      </div>
      <div className="form-row">
        <label>Complimentary session types</label>
        {(config.booking_system.complimentary ?? []).length === 0 ? (
          <p className="hint">
            None. A rule under <code>booking_system.complimentary</code> in the
            studio&apos;s git YAML lets a member book that session type without a
            credit (the booking grants the rule&apos;s zero-price package as a
            comp). Deploy-time, like the catalogs it references.
          </p>
        ) : (
          <ul>
            {(config.booking_system.complimentary ?? []).map((rule) => (
              <li key={rule.session_type}>
                <code>{rule.session_type}</code> — grants{" "}
                <code>{rule.package_slug}</code>, {rule.limit_per_member} per
                member, {rule.min_notice_minutes} min notice
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="form-row">
        <label>Config (JSON; opaque for layer-canonical)</label>
        <textarea
          rows={4}
          value={JSON.stringify(config.booking_system.config, null, 2)}
          onChange={(e) => {
            try {
              const parsed = JSON.parse(e.target.value || "{}");
              if (
                typeof parsed === "object" &&
                parsed !== null &&
                !Array.isArray(parsed)
              ) {
                setConfig({
                  ...config,
                  booking_system: { ...config.booking_system, config: parsed },
                });
              }
            } catch {
              // Ignore parse errors mid-typing; SchemaForm-like power-user
              // shape — operator presses Save when JSON is valid.
            }
          }}
        />
      </div>
    </fieldset>
  );
}

function ExternalIntegrationsSection({
  config,
  setConfig,
}: SectionProps): JSX.Element {
  const k = config.klaviyo ?? {};
  return (
    <fieldset>
      <legend>External integrations (advanced)</legend>
      <p className="hint">
        Klaviyo ingress mapping config (phase 35 sub-decision R). Only visible
        for studios using a non-<code>layer-canonical</code> booking system
        kind — left here for ops visibility.
      </p>
      <div className="form-row">
        <label>session_type_to_duration_minutes (JSON)</label>
        <textarea
          rows={4}
          value={JSON.stringify(
            k.session_type_to_duration_minutes ?? {},
            null,
            2,
          )}
          onChange={(e) => {
            try {
              const parsed = JSON.parse(e.target.value || "{}");
              setConfig({
                ...config,
                klaviyo: { ...k, session_type_to_duration_minutes: parsed },
              });
            } catch {
              // ignore
            }
          }}
        />
      </div>
      <div className="form-row">
        <label>instructor_name_to_trainer_id (JSON; UUID v7 values)</label>
        <textarea
          rows={4}
          value={JSON.stringify(k.instructor_name_to_trainer_id ?? {}, null, 2)}
          onChange={(e) => {
            try {
              const parsed = JSON.parse(e.target.value || "{}");
              setConfig({
                ...config,
                klaviyo: { ...k, instructor_name_to_trainer_id: parsed },
              });
            } catch {
              // ignore
            }
          }}
        />
      </div>
    </fieldset>
  );
}

// ---------------------------------------------------------------------------
// SystemPromptOverrideSection — phase 66c. Rendered as the prominent
// "Agent prompts" section (phase 66d moved it out of the advanced block —
// prompts are core IP and edited regularly).
//
// Live per-studio override of the main chat system prompt: the engine
// skeleton + the member/staff instruction blocks. Each textarea is seeded
// with the code default (from the GET response's `prompt_defaults`) when the
// studio has no override, so an operator edits FROM the real prompt. A save
// that drops a load-bearing `{{placeholder}}` is rejected server-side
// (`studio_config_system_prompt_invalid`, 400) — the hints below list the
// required tokens. The HARD gates (chokepoint actor matrix + tool allowlist
// + book_session confirm-token) are code-enforced, so editing the staff
// block here cannot widen the agent's effective capability.
// ---------------------------------------------------------------------------

const PROMPT_OVERRIDE_MAX = {
  system_prompt: 20000,
  customer_instructions: 8000,
  staff_instructions: 8000,
} as const;

function SystemPromptOverrideSection({
  config,
  setConfig,
  promptDefaults,
}: SectionProps & {
  promptDefaults: {
    system_prompt: string;
    customer_instructions: string;
    staff_instructions: string;
  } | null;
}): JSX.Element {
  const ct = config.chat_translator ?? {};
  // Seed the textarea with the override when present, else the code default.
  // An unedited textarea showing the default is NOT persisted (it only lands
  // in the override row once the operator changes it — see onChange below).
  const seeded = (
    key: "system_prompt" | "customer_instructions" | "staff_instructions",
  ): string => ct[key] ?? promptDefaults?.[key] ?? "";
  const onEdit = (
    key: "system_prompt" | "customer_instructions" | "staff_instructions",
    value: string,
  ): void => {
    // Empty OR exactly-equal-to-default ⇒ drop the override key (use the
    // code default); otherwise persist the edited text.
    const isDefault = value === (promptDefaults?.[key] ?? "");
    setConfig({
      ...config,
      chat_translator: setOrDropAny(
        ct,
        key,
        value === "" || isDefault ? undefined : value,
      ),
    });
  };
  return (
    <fieldset>
      <legend>Agent prompts</legend>
      <p className="hint">
        Live per-studio override of the chat agent's system prompt. Edits are
        live on the next message with no redeploy. Each box is seeded with the
        current default — an unchanged box keeps using the default. The HARD
        safety gates (which tools the agent may call — set per tier under{" "}
        <em>Chat agent actions</em> below — and the destructive-action confirm
        step) are enforced in code, NOT this text, so editing the staff block
        cannot let the agent do anything it couldn't already.
      </p>
      <div className="form-row">
        <label>
          Engine skeleton (max {PROMPT_OVERRIDE_MAX.system_prompt} chars) — MUST
          keep <code>{"{{studio_name}}"}</code>,{" "}
          <code>{"{{current_datetime}}"}</code>,{" "}
          <code>{"{{customer_instructions}}"}</code>,{" "}
          <code>{"{{staff_instructions}}"}</code>.{" "}
          <code>{"{{available_tools}}"}</code> is OPTIONAL — drop it for a
          leaner prompt; the agent still gets its tools via Anthropic&apos;s
          tools array.
        </label>
        <textarea
          maxLength={PROMPT_OVERRIDE_MAX.system_prompt}
          rows={14}
          value={seeded("system_prompt")}
          onChange={(e) => onEdit("system_prompt", e.target.value)}
        />
      </div>
      <div className="form-row">
        <label>
          Member instruction block (max{" "}
          {PROMPT_OVERRIDE_MAX.customer_instructions} chars) — MUST keep{" "}
          <code>{"{{member_id}}"}</code>
        </label>
        <textarea
          maxLength={PROMPT_OVERRIDE_MAX.customer_instructions}
          rows={8}
          value={seeded("customer_instructions")}
          onChange={(e) => onEdit("customer_instructions", e.target.value)}
        />
      </div>
      <div className="form-row">
        <label>
          Staff instruction block (max{" "}
          {PROMPT_OVERRIDE_MAX.staff_instructions} chars) — may reference{" "}
          <code>{"{{studio_name}}"}</code> /{" "}
          <code>{"{{staff_display_name}}"}</code>
        </label>
        <textarea
          maxLength={PROMPT_OVERRIDE_MAX.staff_instructions}
          rows={10}
          value={seeded("staff_instructions")}
          onChange={(e) => onEdit("staff_instructions", e.target.value)}
        />
      </div>
    </fieldset>
  );
}

// ---------------------------------------------------------------------------
// KnowledgeBaseSection — phase 41a section #8.
//
// Per-topic row: [topic-slug input | content textarea | delete button].
// Add topic button at the bottom. Per-row character counter on the
// content (yellow at 9000, red at 10240). Topic-slug client-side
// validation: kebab-case (^[a-z][a-z0-9-]{0,59}$); duplicate detection
// surfaces inline as a row-level warning. Save (the page-level Save)
// collects rows, drops fully-empty rows (both topic + content blank),
// and writes through `POST /api/studio-config/:slug`.
// ---------------------------------------------------------------------------

const KB_TOPIC_REGEX = /^[a-z][a-z0-9-]{0,59}$/;
const KB_CONTENT_MAX_CHARS = 10240;
const KB_CONTENT_WARN_AT = 9000;

function KnowledgeBaseSection({
  config,
  setConfig,
}: SectionProps): JSX.Element {
  const entries = config.knowledge_base ?? [];

  // Topic-slug uniqueness detection — flag rows whose topic matches an
  // earlier row's topic. Allow empty topics (operator may still be
  // typing); only flag non-empty duplicates.
  const dupes = new Set<number>();
  const seen = new Map<string, number>();
  for (let i = 0; i < entries.length; i += 1) {
    const e = entries[i];
    if (e === undefined) continue;
    const t = e.topic.trim();
    if (t === "") continue;
    if (seen.has(t)) {
      dupes.add(i);
      const earlier = seen.get(t)!;
      dupes.add(earlier);
    } else {
      seen.set(t, i);
    }
  }

  const updateRow = (
    i: number,
    patch: Partial<KnowledgeBaseEntry>,
  ): void => {
    const next = entries.slice();
    const current = next[i];
    if (current === undefined) return;
    next[i] = { ...current, ...patch };
    setConfig({ ...config, knowledge_base: next });
  };

  const deleteRow = (i: number, topic: string): void => {
    const label = topic.trim() !== "" ? `'${topic}'` : "(unnamed)";
    if (!window.confirm(`Delete topic ${label}?`)) return;
    const next = entries.slice();
    next.splice(i, 1);
    setConfig({ ...config, knowledge_base: next });
  };

  const addRow = (): void => {
    const next = entries.slice();
    next.push({ topic: "", content: "" });
    setConfig({ ...config, knowledge_base: next });
  };

  return (
    <fieldset>
      <legend>Knowledge base — answers for the AI chat agent</legend>
      <p className="hint">
        <em>
          Add topics like <code>opening-hours</code> or{" "}
          <code>cancellation-policy</code>. The AI agent reads these when
          customers ask factual questions.
        </em>
      </p>

      {entries.length === 0 && (
        <p>
          No knowledge base entries yet. Click <strong>Add topic</strong> to
          create one.
        </p>
      )}

      {entries.map((e, i) => {
        const topicValid =
          e.topic.length === 0 || KB_TOPIC_REGEX.test(e.topic);
        const contentLen = e.content.length;
        const overLimit = contentLen > KB_CONTENT_MAX_CHARS;
        const nearLimit =
          contentLen >= KB_CONTENT_WARN_AT && !overLimit;
        const isDuplicate = dupes.has(i);
        return (
          <div key={i} className="kb-row">
            <div className="form-row">
              <label>
                topic <small>(kebab-case, max 60 chars)</small>
              </label>
              <input
                type="text"
                value={e.topic}
                onChange={(ev) => updateRow(i, { topic: ev.target.value })}
                placeholder="opening-hours"
                pattern="^[a-z][a-z0-9-]{0,59}$"
              />
              {!topicValid && (
                <small className="tag-danger">
                  Must be kebab-case: lowercase letters, digits, hyphens; first
                  char a-z; max 60 chars.
                </small>
              )}
              {isDuplicate && (
                <small className="tag-danger">
                  Duplicate topic — each slug must be unique.
                </small>
              )}
            </div>
            <div className="form-row">
              <label>content (markdown)</label>
              <textarea
                rows={Math.min(
                  30,
                  Math.max(4, e.content.split("\n").length + 1),
                )}
                value={e.content}
                onChange={(ev) =>
                  updateRow(i, { content: ev.target.value })
                }
                className={`kb-content${overLimit ? " kb-content-over" : ""}`}
              />
              <small
                className={`kb-counter${
                  overLimit
                    ? " kb-counter-error"
                    : nearLimit
                      ? " kb-counter-warn"
                      : ""
                }`}
              >
                {contentLen} / {KB_CONTENT_MAX_CHARS}
              </small>
            </div>
            <div className="form-row">
              <button
                type="button"
                onClick={() => deleteRow(i, e.topic)}
                className="tag-danger"
              >
                Delete topic
              </button>
            </div>
          </div>
        );
      })}

      <p>
        <button type="button" onClick={addRow}>
          Add topic
        </button>
      </p>
    </fieldset>
  );
}

// ---------------------------------------------------------------------------
// ConnectorsSection — 2026-07-27, Operations → Connectors.
//
// One checkbox per connector. UNCHECKING is a FULL pause in both directions:
// the outbound canonical actions refuse with `connector_disabled`, the
// vendor's inbound webhooks are acknowledged (HTTP 200) but skipped, and its
// cron scans stop. Rides the page-level YAML Save (`connectors` is a live
// `studio_config` override field, not a catalog key), so a pause takes effect
// with no redeploy.
//
// Rows derive from the canonical CONNECTOR_DESCRIPTORS so this section can
// never drift behind an added connector — the failure the vendor-credentials
// section shipped by hand-copying its kind list.
// ---------------------------------------------------------------------------


function ConnectorsSection({ config, setConfig }: SectionProps): JSX.Element {
  const connectors = config.connectors;
  // The website-chat row's pause reads `studio.webchat_enabled`, NOT the
  // connectors map: absent means OFF here (the member-api endpoint's own
  // default), the inverse of every other connector's default-enabled rule.
  const webchatEnabled =
    (config.studio as Record<string, unknown> | undefined)?.[
      "webchat_enabled"
    ] === true;

  const setEnabled = (key: ConnectorKey, enabled: boolean): void => {
    const nextConnectors: Partial<Record<ConnectorKey, ConnectorSettings>> = {
      ...(connectors ?? {}),
    };
    const entry: { enabled?: boolean; autonomy?: AiAutonomyMode } = {
      ...(nextConnectors[key] ?? {}),
    };
    if (enabled) {
      // Default IS enabled — drop the flag rather than persisting `true`, so
      // the override row stays minimal and a re-enable leaves no residue. Any
      // stored `autonomy` survives (this path is only reached by the plain
      // rows, which have none, but the shape is shared).
      delete entry.enabled;
    } else {
      entry.enabled = false;
    }
    if (Object.keys(entry).length === 0) {
      delete nextConnectors[key];
    } else {
      nextConnectors[key] = entry;
    }
    const next = { ...config };
    if (Object.keys(nextConnectors).length === 0) {
      delete next.connectors;
    } else {
      next.connectors = nextConnectors;
    }
    setConfig(next);
  };

  /**
   * A conversational connector's four states collapse onto the TWO fields that
   * already govern it — the connector pause and its `autonomy` — both of which
   * now live in the SAME object, so one write covers both:
   *
   *   off            → connectors.<key>.enabled = false
   *   notify         → connector on, autonomy 'notify'
   *   booking_intent → connector on, autonomy 'booking_intent'
   *   all            → connector on, autonomy 'all'
   *
   * Each half drops its key at that connector's OWN default, so a studio that
   * never moves off the default persists nothing and matches a fresh studio
   * exactly. Turning the connector OFF leaves the stored autonomy untouched,
   * so switching it back on restores the operator's previous choice rather
   * than silently resetting it.
   */
  // The transform itself lives in lib/studio-config-patch.ts so `/agents` can
  // write the same ladder without a second copy of the drop-at-default rules.
  // One implementation, two call sites, one unit test.
  const setConnectorAiState = (
    key: ConnectorKey,
    next: ConnectorAiState,
  ): void => {
    setConfig(applyConnectorState(config, key, next));
  };

  const setConnectorCapability = (
    key: ConnectorKey,
    next: ConnectorCapability,
  ): void => {
    setConfig(applyConnectorCapability(config, key, next));
  };

  /**
   * The capability-only chat row's three states write TWO different keys: the
   * pause is `studio.webchat_enabled` (the gate the member-api endpoint has
   * enforced since phase 88 — reused rather than duplicated into
   * `connectors.webchat.enabled`, which the validator rejects), while the two
   * capability states write `connectors.webchat.capability`.
   *
   * Picking a capability also turns the surface ON: with a single radio group
   * "Answer only" cannot sensibly leave the widget paused, and the operator
   * who clicks it plainly wants a chat that answers. Turning it OFF leaves the
   * stored capability alone, so switching back on restores their choice —
   * the same survive-a-pause posture the connector ladder has.
   */
  const setConnectorChatState = (
    key: ConnectorKey,
    next: ConnectorChatState,
  ): void => {
    if (next === "off") {
      setConfig(applyWebchatEnabled(config, false));
      return;
    }
    setConfig(applyWebchatEnabled(applyConnectorCapability(config, key, next), true));
  };

  const outboundEnv: StripeOutboundEnvironment =
    config.stripe?.outbound_environment ?? "test";

  const setOutboundEnv = (env: StripeOutboundEnvironment): void => {
    const nextStripe = { ...(config.stripe ?? {}) };
    if (env === "test") {
      // 'test' is the default — drop the key rather than persist it.
      delete nextStripe.outbound_environment;
    } else {
      nextStripe.outbound_environment = env;
    }
    const next = { ...config };
    if (Object.keys(nextStripe).length === 0) {
      delete next.stripe;
    } else {
      next.stripe = nextStripe;
    }
    setConfig(next);
  };

  return (
    <fieldset className="connectors-section">
      <legend>Connectors — activate / deactivate per vendor</legend>
      <p>
        Unticking a connector is a <strong>full pause</strong>: Layer stops
        calling that vendor, stops processing its incoming webhooks, and stops
        its scheduled scans. Saves take effect immediately — no redeploy.
        Credentials are left untouched, so re-ticking resumes where it left
        off.
      </p>
      <p style={{ color: "#8a5a00" }}>
        A paused connector also fails any <em>new</em> workflow that depends on
        it (a booking whose confirmation message can't send, for example) —
        the same as removing its credentials. In-flight work still rolls back
        cleanly.
      </p>

      {CONNECTOR_DESCRIPTORS.map((d) => {
        const enabled = isConnectorEnabled(connectors, d.key);
        // A conversational connector's on/off is not the whole story: being
        // reachable and letting the assistant answer are separate decisions,
        // so its row is a FOUR-state control rather than a checkbox. Every
        // other connector stays a plain tick. Which connectors those are is
        // the descriptor's own `ai` block — never a list repeated here.
        if (d.ai !== undefined) {
          return (
            <ConnectorAiRow
              key={d.key}
              descriptor={d}
              enabled={enabled}
              autonomy={resolveConnectorAutonomy(connectors, d.key)}
              capability={resolveConnectorCapability(connectors, d.key)}
              onChange={(next) => setConnectorAiState(d.key, next)}
              onCapabilityChange={(next) => setConnectorCapability(d.key, next)}
            />
          );
        }
        // A capability-only chat surface: three states, and its pause is the
        // studio flag rather than `isConnectorEnabled` — reading the wrong one
        // here would render a row that always claims to be on.
        if (d.chat !== undefined) {
          return (
            <ConnectorChatRow
              key={d.key}
              descriptor={d}
              enabled={webchatEnabled}
              capability={resolveConnectorCapability(connectors, d.key)}
              onChange={(next) => setConnectorChatState(d.key, next)}
            />
          );
        }
        return (
          <div
            key={d.key}
            className="connector-row"
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 10,
              padding: "8px 0",
              borderTop: "1px solid #eee",
            }}
          >
            <input
              type="checkbox"
              id={`connector-${d.key}`}
              checked={enabled}
              onChange={(e) => setEnabled(d.key, e.target.checked)}
              style={{ marginTop: 3 }}
            />
            <div style={{ flex: "1 1 auto" }}>
              <label
                htmlFor={`connector-${d.key}`}
                style={{ fontWeight: 600, cursor: "pointer" }}
              >
                {d.label}
              </label>
              {!enabled && (
                <span
                  className="tag tag-danger"
                  style={{ marginLeft: 8, fontSize: "0.8em" }}
                >
                  paused
                </span>
              )}
              <div style={{ fontSize: "0.85em", color: "#5c6069", marginTop: 2 }}>
                {d.pauses}
              </div>
            </div>
          </div>
        );
      })}

      <div
        style={{
          marginTop: 16,
          paddingTop: 10,
          borderTop: "2px solid #eee",
        }}
      >
        <label htmlFor="stripe-outbound-env" style={{ fontWeight: 600 }}>
          Stripe outbound account — which account real charges hit
        </label>
        <div style={{ fontSize: "0.85em", color: "#5c6069", margin: "2px 0 6px" }}>
          Charges, refunds and Checkout Sessions run against this account.
          Chosen explicitly and never inferred: a paused or unkeyed account
          makes the charge <em>refuse</em> rather than quietly fall through to
          the other one.
        </div>
        <select
          id="stripe-outbound-env"
          value={outboundEnv}
          onChange={(e) =>
            setOutboundEnv(e.target.value as StripeOutboundEnvironment)
          }
        >
          <option value="test">Test — sandbox account (no real money)</option>
          <option value="live">Live — CodexFit account (REAL money)</option>
        </select>
        {outboundEnv === "live" && (
          <div
            className="tag tag-danger"
            style={{ marginLeft: 8, fontSize: "0.85em" }}
          >
            real customer cards will be charged
          </div>
        )}
      </div>
    </fieldset>
  );
}

// ---------------------------------------------------------------------------
// GmailMailboxesSection — 2026-08-07, Integrations → Mailboxes.
//
// The `gmail.mailboxes` list, editable. Until now it was reachable ONLY by a
// hand-built `edit_studio_config` patch, which is also the one path that could
// silently delete the whole block (see the anti-wipe guard's optional leg) —
// so the block had the worst possible combination: precious, load-bearing, and
// edited by the riskiest surface available.
//
// Shaped on KnowledgeBaseSection (rows + Add + a confirm on Delete) rather than
// StaffAllowlistSection: `gmail` is a `studio_config` override field and rides
// the PAGE Save, not a second instant-save endpoint.
//
// Three things this section has to get right, all of them backend contracts:
//
//   1. `send_as` IS A SECURITY BOUNDARY, not a preference. Gmail answers HTTP
//      200 and SILENTLY REWRITES a `From:` that is not a verified alias on the
//      scanning mailbox (proven live 2026-08-01: an unverified sender arrived
//      as the mailbox's primary address). Nothing in the logs looks wrong. So
//      turning it ON asks for a confirmation and the row says why.
//   2. `member_mail: false` must be DROPPED, never written. The validator
//      drops it (absent ≡ false), so persisting `false` would make every Save
//      look dirty against a config that never carries it. Same drop-at-default
//      idiom as ConnectorsSection.
//   3. The block's key set is CLOSED on the backend and a Save is LIVE with no
//      deploy, so a malformed block reaches production the moment it is saved.
//      Rows therefore write exactly the five known keys and nothing else.
//
// Client-side checks here are ADVISORY (the chokepoint is the sole enforcement
// layer, invariant 2): they surface the same rules inline so an operator sees
// the problem before the round-trip, and the backend still refuses.
// ---------------------------------------------------------------------------

/** Same shape check the backend applies to an address (`FROM_ADDRESS_REGEX`). */
const GMAIL_ADDRESS_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const GMAIL_LABEL_MAX_CHARS = 40;

function GmailMailboxesSection({ config, setConfig }: SectionProps): JSX.Element {
  const gmail = config.gmail;
  const entries: GmailMailboxShape[] = gmail?.mailboxes ?? [];

  /** Case-insensitive duplicate detection — the backend's own comparison. */
  const dupeIndices = (pick: (m: GmailMailboxShape) => string): Set<number> => {
    const out = new Set<number>();
    const seen = new Map<string, number>();
    for (let i = 0; i < entries.length; i += 1) {
      const e = entries[i];
      if (e === undefined) continue;
      const key = pick(e).trim().toLowerCase();
      if (key === "") continue;
      const earlier = seen.get(key);
      if (earlier !== undefined) {
        out.add(i);
        out.add(earlier);
      } else {
        seen.set(key, i);
      }
    }
    return out;
  };
  const dupeAddresses = dupeIndices((m) => m.address);
  const dupeLabels = dupeIndices((m) => m.label);

  /** Write the list back, dropping the block entirely when it empties out. */
  const commit = (next: GmailMailboxShape[]): void => {
    const draft = { ...config };
    const nextGmail = { ...(gmail ?? {}) };
    if (next.length === 0) {
      delete nextGmail.mailboxes;
    } else {
      nextGmail.mailboxes = next;
    }
    if (Object.keys(nextGmail).length === 0) {
      delete draft.gmail;
    } else {
      draft.gmail = nextGmail;
    }
    setConfig(draft);
  };

  const updateRow = (i: number, patch: Partial<GmailMailboxShape>): void => {
    const next = entries.slice();
    const current = next[i];
    if (current === undefined) return;
    const merged: GmailMailboxShape = { ...current, ...patch };
    // `member_mail` false ≡ absent (see the header). Drop rather than persist.
    if (merged.member_mail !== true) delete merged.member_mail;
    next[i] = merged;
    commit(next);
  };

  const setSendAs = (i: number, next: boolean): void => {
    const row = entries[i];
    if (row === undefined) return;
    if (
      next &&
      !window.confirm(
        `Allow replies to leave AS ${row.address || "this address"}?\n\n` +
          "This is a security boundary, not a preference. The address must " +
          "already be a VERIFIED send-as alias (or the primary identity) on " +
          "the scanning mailbox. If it is not, Gmail returns success and " +
          "silently rewrites the From: header to the mailbox's own address — " +
          "mail ships under the wrong identity with nothing wrong in the logs.",
      )
    ) {
      return;
    }
    updateRow(i, { send_as: next });
  };

  const deleteRow = (i: number): void => {
    const row = entries[i];
    if (row === undefined) return;
    const name = row.address.trim() !== "" ? row.address : "(unnamed)";
    if (
      !window.confirm(
        `Remove the mailbox ${name}?\n\n` +
          (row.scan
            ? "It is SCANNED, so removing it stops the inbox scanner reading " +
              "it and its tab disappears from /inbox. "
            : "") +
          "Replies can no longer be sent as this address either.",
      )
    ) {
      return;
    }
    const next = entries.slice();
    next.splice(i, 1);
    commit(next);
  };

  const addRow = (): void => {
    // Both required flags start FALSE: an added row must be opted in
    // deliberately, never arrive already scanning or already sendable.
    commit([...entries, { address: "", label: "", scan: false, send_as: false }]);
  };

  return (
    <fieldset>
      <legend>Mailboxes — which addresses Layer reads and replies as</legend>
      <p className="hint">
        <em>
          One row per address this studio&apos;s Google Workspace key acts for.
          Saved changes are <strong>live immediately</strong> — no redeploy.
        </em>
      </p>

      {gmail?.impersonate_user !== undefined && (
        <p className="hint">
          Acting as <code>{gmail.impersonate_user}</code> — the Workspace
          mailbox the service-account key impersonates. Changed in the studio
          YAML, not here.
        </p>
      )}

      {entries.length === 0 && (
        <p>
          No mailboxes declared. Nothing is scanned and no reply identity is
          allowed. Click <strong>Add mailbox</strong> to declare one.
        </p>
      )}

      {entries.map((e, i) => {
        const addressValid =
          e.address.length === 0 || GMAIL_ADDRESS_REGEX.test(e.address);
        const labelTooLong = e.label.length > GMAIL_LABEL_MAX_CHARS;
        return (
          <div key={i} className="kb-row">
            <div className="form-row">
              <label>address</label>
              <input
                type="email"
                value={e.address}
                onChange={(ev) => updateRow(i, { address: ev.target.value })}
                placeholder="contact@e-pulsive.co.uk"
              />
              {!addressValid && (
                <small className="tag-danger">
                  Must look like a valid email address.
                </small>
              )}
              {dupeAddresses.has(i) && (
                <small className="tag-danger">
                  Duplicate address — each mailbox must appear once.
                </small>
              )}
            </div>

            <div className="form-row">
              <label>
                label <small>(shown as the tab name on /inbox, max 40)</small>
              </label>
              <input
                type="text"
                value={e.label}
                onChange={(ev) => updateRow(i, { label: ev.target.value })}
                placeholder="business"
                maxLength={GMAIL_LABEL_MAX_CHARS}
              />
              {labelTooLong && (
                <small className="tag-danger">
                  Max {GMAIL_LABEL_MAX_CHARS} characters.
                </small>
              )}
              {dupeLabels.has(i) && (
                <small className="tag-danger">
                  Duplicate label — two tabs with one name is a silently-wrong
                  board.
                </small>
              )}
            </div>

            <div className="form-row">
              <label>
                <input
                  type="checkbox"
                  checked={e.scan}
                  onChange={(ev) => updateRow(i, { scan: ev.target.checked })}
                />{" "}
                Scan this mailbox
              </label>
              <small className="hint">
                Read new mail arriving here and triage it onto /inbox. A newly
                scanned mailbox starts from now — it never pulls its backlog.
              </small>
            </div>

            <div className="form-row">
              <label>
                <input
                  type="checkbox"
                  checked={e.send_as}
                  onChange={(ev) => setSendAs(i, ev.target.checked)}
                />{" "}
                Send replies as this address
              </label>
              {e.send_as && (
                <span
                  className="tag tag-danger"
                  style={{ marginLeft: 8, fontSize: "0.8em" }}
                >
                  verified alias required
                </span>
              )}
              <small className="hint">
                Must already be a verified send-as alias (or the primary
                identity) on the scanning mailbox. If it is not, Gmail reports
                success and silently rewrites the From: header — mail leaves
                under the wrong identity and the logs look clean.
              </small>
            </div>

            <div className="form-row">
              <label>
                <input
                  type="checkbox"
                  checked={e.member_mail === true}
                  onChange={(ev) =>
                    updateRow(i, { member_mail: ev.target.checked })
                  }
                />{" "}
                Show member mail sent here
              </label>
              <small className="hint">
                Off by default. When a known member emails this address, open a
                matter on /inbox for a human instead of dropping it. The AI
                assistant never drafts a reply to a member either way — this
                only decides whether anyone SEES it.
              </small>
            </div>

            <div className="form-row">
              <button
                type="button"
                onClick={() => deleteRow(i)}
                className="tag-danger"
              >
                Remove mailbox
              </button>
            </div>
          </div>
        );
      })}

      <p>
        <button type="button" onClick={addRow}>
          Add mailbox
        </button>
      </p>
    </fieldset>
  );
}

// ---------------------------------------------------------------------------
// VendorCredentialsSection — phase 48 section #9.
//
// Per-kind row: [kind label | masked input | Reveal button | Delete button].
// Reveal dispatches `reveal_studio_secret` through `/api/studio-secrets/:slug/reveal/:kind`;
// on success swaps the field to plaintext + shows a Copy button that
// calls `navigator.clipboard.writeText(value)` + flashes `Copied ✓` for 2s.
// Save (the page-level Save) collects pending changes + writes through
// `POST /api/studio-secrets/:slug` (DIFFERENT endpoint from `edit_studio_config`).
// ---------------------------------------------------------------------------

/**
 * Rows derive from the canonical `KNOWN_SECRET_KINDS` (single source of
 * truth in `@layer/canonical-data`) — 2026-07-27, replacing a hand-copied
 * 11-entry list frozen at phase 48 that silently dropped every kind added
 * since (telegram, instagram, gmail, analytics, the live Stripe webhook
 * secret). The backend's `list_studio_secrets_presence` already returns
 * presence for the full set; deriving here makes UI drift impossible.
 * Canonical order is human-grouped by vendor, so prefix changes double as
 * group boundaries.
 */
const KNOWN_SECRET_KINDS_UI: ReadonlyArray<{ kind: string; label: string }> =
  KNOWN_SECRET_KINDS.map((kind) => ({
    kind,
    label: KNOWN_SECRET_KIND_LABELS[kind],
  }));

/**
 * Per-kind operator hints for kinds whose "not set" state is expected
 * rather than a gap.
 */
const SECRET_KIND_HINTS: Readonly<Record<string, string>> = {
  "telegram.bot_token":
    "Internal PA/staff-alert channel — the token usually lives in env " +
    "(LAYER_TELEGRAM_BOT_TOKEN), so 'not set' here is expected.",
};

/** Vendor prefix of a secret kind (`stripe.api_key` → `stripe`). */
function secretKindVendor(kind: string): string {
  const dot = kind.indexOf(".");
  return dot === -1 ? kind : kind.slice(0, dot);
}

interface VendorCredentialsSectionProps {
  readonly slug: string;
  /**
   * Local pending edits — kept on the page so the page-level Save
   * commits secrets alongside the YAML config. Two distinct dispatches
   * fire; per phase 48 sub-decision J they may both succeed or one may
   * fail independently.
   */
  readonly pendingSecrets: Record<string, string | null>;
  readonly setPendingSecrets: (
    next: Record<string, string | null>,
  ) => void;
}

function VendorCredentialsSection({
  slug,
  pendingSecrets,
  setPendingSecrets,
}: VendorCredentialsSectionProps): JSX.Element {
  const [revealedValues, setRevealedValues] = useState<
    Record<string, string>
  >({});
  const [copyConfirm, setCopyConfirm] = useState<Record<string, boolean>>({});
  const [editingKinds, setEditingKinds] = useState<Record<string, boolean>>({});

  // Phase 67 — secrets presence is cache-backed (key
  // `studio-secrets:<slug>`) so the section renders the last-seen
  // present/updated_at state instantly on return; `saveStudioSecrets`
  // invalidates this key so the refreshed presence loads after a write.
  const { data: secretsResp, error: secretsErr } = useCachedResource(
    `studio-secrets:${slug}`,
    () => api.getStudioSecrets(slug),
  );
  const presence = useMemo<
    ReadonlyMap<string, { present: boolean; updated_at: string | null }>
  >(() => {
    const map = new Map<
      string,
      { present: boolean; updated_at: string | null }
    >();
    if (secretsResp !== undefined) {
      for (const s of secretsResp.secrets) {
        map.set(s.kind, { present: s.present, updated_at: s.updated_at });
      }
    }
    return map;
  }, [secretsResp]);
  const loadError = secretsErr?.message ?? null;

  const handleReveal = async (kind: string): Promise<void> => {
    try {
      const requestId = crypto.randomUUID();
      const resp = await api.revealStudioSecret(slug, kind, {
        request_id: requestId,
      });
      if (resp.value !== null) {
        setRevealedValues((cur) => ({ ...cur, [kind]: resp.value! }));
        setEditingKinds((cur) => ({ ...cur, [kind]: true }));
      } else {
        window.alert(
          `No value set for ${kind} — click 'Add' instead of Reveal.`,
        );
      }
    } catch (e) {
      window.alert(`Reveal failed: ${(e as Error).message}`);
    }
  };

  const handleCopy = (kind: string): void => {
    const value = revealedValues[kind] ?? "";
    if (value === "") return;
    void navigator.clipboard.writeText(value);
    setCopyConfirm((cur) => ({ ...cur, [kind]: true }));
    setTimeout(() => {
      setCopyConfirm((cur) => ({ ...cur, [kind]: false }));
    }, 2000);
  };

  const handleEditValue = (kind: string, newValue: string): void => {
    setRevealedValues((cur) => ({ ...cur, [kind]: newValue }));
    setPendingSecrets({ ...pendingSecrets, [kind]: newValue });
  };

  const handleDelete = (kind: string, label: string): void => {
    if (!window.confirm(`Delete ${label}? This drops the studio_secrets row.`))
      return;
    setPendingSecrets({ ...pendingSecrets, [kind]: null });
    setRevealedValues((cur) => {
      const next = { ...cur };
      delete next[kind];
      return next;
    });
    setEditingKinds((cur) => {
      const next = { ...cur };
      delete next[kind];
      return next;
    });
  };

  const handleAdd = (kind: string): void => {
    setEditingKinds((cur) => ({ ...cur, [kind]: true }));
    setRevealedValues((cur) => ({ ...cur, [kind]: "" }));
  };

  return (
    <fieldset className="secrets-section">
      <legend>Vendor credentials — secrets used by webhook receivers + outbound API calls</legend>
      <p>
        Values are stored as plaintext under RLS + Supabase disk-encryption.
        Click <strong>Reveal</strong> to see a value; click <strong>Copy</strong>{" "}
        to copy it to clipboard.
      </p>
      {loadError !== null && (
        <div className="tag tag-danger">Failed to load presence: {loadError}</div>
      )}
      {KNOWN_SECRET_KINDS_UI.map(({ kind, label }, idx) => {
        const vendor = secretKindVendor(kind);
        const prevKind = KNOWN_SECRET_KINDS_UI[idx - 1]?.kind;
        const isNewVendorGroup =
          prevKind === undefined || secretKindVendor(prevKind) !== vendor;
        const hint = SECRET_KIND_HINTS[kind];
        const pres = presence.get(kind);
        const isPending = Object.prototype.hasOwnProperty.call(
          pendingSecrets,
          kind,
        );
        const pendingValue = isPending ? pendingSecrets[kind] : undefined;
        const isDeleted = pendingValue === null;
        const isEditing = editingKinds[kind] === true;
        const present = pres?.present ?? false;
        const displayValue =
          revealedValues[kind] ??
          (pendingValue !== undefined && pendingValue !== null
            ? pendingValue
            : "");
        return (
          <Fragment key={kind}>
            {isNewVendorGroup && (
              <div
                className="secret-vendor-group"
                style={{
                  marginTop: 12,
                  paddingTop: 6,
                  borderTop: "1px solid #e5e5e5",
                  fontSize: "0.8em",
                  fontWeight: 600,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  color: "#5c6069",
                }}
              >
                {vendor}
              </div>
            )}
            <div
              className="secret-row"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "4px 0",
              }}
            >
            <div style={{ flex: "0 0 240px" }}>
              <label>{label}</label>
              {hint !== undefined && (
                <div style={{ fontSize: "0.8em", color: "#82858c" }}>{hint}</div>
              )}
              {pres?.updated_at !== null && pres?.updated_at !== undefined && (
                <div style={{ fontSize: "0.85em", color: "#5c6069" }}>
                  updated {pres.updated_at}
                </div>
              )}
            </div>
            <div style={{ flex: "1 1 auto" }}>
              {isDeleted ? (
                <span className="tag tag-danger">
                  pending delete (Save to confirm)
                </span>
              ) : isEditing || isPending ? (
                <input
                  type={isEditing ? "text" : "password"}
                  value={displayValue}
                  onChange={(e) => handleEditValue(kind, e.target.value)}
                  placeholder="paste plaintext value"
                  style={{ width: "100%" }}
                  autoComplete="off"
                />
              ) : present ? (
                <input
                  type="password"
                  value="••••••••"
                  readOnly
                  style={{ width: "100%" }}
                />
              ) : (
                <span style={{ color: "#90939a" }}>not set</span>
              )}
            </div>
            <div style={{ flex: "0 0 auto", display: "flex", gap: 4 }}>
              {present && !isDeleted && !isEditing && (
                <button
                  type="button"
                  className="secret-reveal-btn"
                  onClick={() => handleReveal(kind)}
                >
                  Reveal
                </button>
              )}
              {!present && !isDeleted && !isEditing && (
                <button type="button" onClick={() => handleAdd(kind)}>
                  Add
                </button>
              )}
              {isEditing && revealedValues[kind] !== undefined && (
                <button
                  type="button"
                  className="secret-copy-btn"
                  onClick={() => handleCopy(kind)}
                >
                  {copyConfirm[kind] === true ? "Copied ✓" : "Copy"}
                </button>
              )}
              {(present || isPending) && !isDeleted && (
                <button
                  type="button"
                  className="secret-delete-btn"
                  onClick={() => handleDelete(kind, label)}
                  title="Delete this kind"
                >
                  🗑
                </button>
              )}
              {isDeleted && (
                <button
                  type="button"
                  onClick={() => {
                    const next = { ...pendingSecrets };
                    delete next[kind];
                    setPendingSecrets(next);
                  }}
                >
                  Undo
                </button>
              )}
            </div>
            </div>
          </Fragment>
        );
      })}
    </fieldset>
  );
}

// ---------------------------------------------------------------------------
// Page shell.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Console layout (to-do #4 — "Console" direction Mario picked 2026-07-07).
//
// A settings-app console: a grouped section rail on the left, one focused
// section form in the main pane, and a search box to jump anywhere. The active
// section persists per studio in localStorage. Each section still renders its
// own `<fieldset><legend>` — the pane header supplies the visible title, so the
// redundant inner legend + fieldset border are neutralised in CSS
// (`.config-console-body > fieldset`). Save semantics are unchanged: the page-
// level "Save all changes" writes the WHOLE YAML (full-replace).
// ---------------------------------------------------------------------------

/** Stable id → full header title (shown at the top of the main pane). */
const SECTION_TITLES: Readonly<Record<string, string>> = {
  contact: "Contact information",
  timezone: "Studio timezone",
  branding: "Branding",
  pricing: "Pricing — packages & memberships",
  "session-types": "Session types",
  ai: "AI",
  "system-prompt": "Agent prompts",
  "chat-tools": "Chat agent actions",
  memory: "Memory",
  automations: "Automations (read-only)",
  connectors: "Connectors — activate / deactivate per vendor",
  "staff-allowlist": "Staff allowlist · WhatsApp → human:staff promotion",
  "personal-trainers": "Personal trainers · staff who deliver sessions",
  "trainer-payroll": "Trainer payroll · pay models, rates & bonus",
  "vendor-credentials": "Vendor credentials — secrets for webhooks + outbound API",
  mailboxes: "Mailboxes — which addresses Layer reads and replies as",
  "knowledge-base": "Knowledge base — answers for the AI chat agent",
  "marketing-flows": "Marketing flows · per-studio toggles",
  "booking-system": "Booking system",
  "external-integrations": "External integrations",
};

/** Stable id → short nav label (the left-rail list in the Console layout). */
const SECTION_NAV_LABEL: Readonly<Record<string, string>> = {
  contact: "Contact",
  timezone: "Timezone",
  branding: "Branding",
  pricing: "Pricing",
  "session-types": "Session types",
  ai: "AI voice",
  "system-prompt": "Agent prompts",
  "chat-tools": "Chat tools",
  memory: "Memory",
  "knowledge-base": "Knowledge base",
  "staff-allowlist": "Staff allowlist",
  "personal-trainers": "Trainers",
  "trainer-payroll": "Payroll",
  "booking-system": "Booking system",
  "marketing-flows": "Marketing flows",
  automations: "Automations",
  connectors: "Connectors",
  "vendor-credentials": "Vendor credentials",
  mailboxes: "Mailboxes",
  "external-integrations": "External integrations",
};

/** One-line pane subtitle per section (what it controls). */
const SECTION_SUBTITLE: Readonly<Record<string, string>> = {
  contact: "Studio contact details shown to members + used in outbound comms.",
  timezone: "The studio's local timezone — drives every displayed time + slot.",
  branding: "Studio name, logo, and colours.",
  pricing: "Packages & memberships. Catalog fields save from the git YAML on deploy.",
  "session-types": "The bookable session types offered at this studio.",
  ai: "Brand voice + behaviour of the WhatsApp chat agent.",
  "system-prompt": "The agent's system + customer + staff instructions. Live override active.",
  "chat-tools": "Which canonical actions the chat agent may call, per tier.",
  memory: "Per-member + per-trainer memory the agent may read/write.",
  "knowledge-base": "Answers the AI chat agent can look up by topic.",
  "staff-allowlist": "WhatsApp numbers promoted to human:staff, plus the admin tier.",
  "personal-trainers": "Staff who deliver sessions.",
  "trainer-payroll":
    "Pay models, rates & bonus percentages. Applies from the next payroll run.",
  "booking-system": "Availability source, timezone, and booking behaviour.",
  "marketing-flows": "Per-studio marketing-flow toggles.",
  automations: "Scheduled jobs running for this studio (read-only).",
  connectors:
    "Pause or resume a vendor integration. Takes effect immediately \u2014 no redeploy.",
  "vendor-credentials": "Secrets for webhooks + outbound vendor APIs.",
  mailboxes:
    "Email addresses Layer scans, replies as, and shows member mail from. Live on Save — no redeploy.",
  "external-integrations": "Non-canonical external integrations.",
};

/** Grouped nav — the ordered left-rail structure. Only live ids render. */
const SECTION_GROUPS: ReadonlyArray<{ group: string; ids: string[] }> = [
  { group: "Essentials", ids: ["contact", "timezone", "branding", "pricing", "session-types"] },
  { group: "AI & chat", ids: ["ai", "system-prompt", "chat-tools", "memory", "knowledge-base"] },
  { group: "People", ids: ["staff-allowlist", "personal-trainers", "trainer-payroll"] },
  { group: "Operations", ids: ["booking-system", "marketing-flows", "automations", "connectors"] },
  {
    group: "Integrations",
    ids: ["vendor-credentials", "mailboxes", "external-integrations"],
  },
];

const activeSectionKey = (slug: string | undefined): string =>
  `studio-config-active-section:${slug ?? "__none__"}`;

function loadString(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null; // localStorage unavailable (private mode) — non-fatal.
  }
}

/** A small count/state badge for a nav item, computed from the loaded config. */
function navBadge(id: string, config: StudioL2ConfigShape): string | null {
  const ct = config.chat_translator ?? {};
  switch (id) {
    case "pricing": {
      const n = (config.packages?.length ?? 0) + (config.memberships?.length ?? 0);
      return n > 0 ? String(n) : null;
    }
    case "session-types": {
      const n = config.session_types?.length ?? 0;
      return n > 0 ? String(n) : null;
    }
    case "chat-tools": {
      const n =
        ct.tool_allowlist_member?.length ??
        ct.tool_allowlist?.length ??
        0;
      return n > 0 ? String(n) : null;
    }
    case "knowledge-base": {
      const n = config.knowledge_base?.length ?? 0;
      return n > 0 ? String(n) : null;
    }
    case "connectors": {
      // Paused connectors are the state an operator must not forget about —
      // a silently-paused vendor looks identical to a broken one.
      const paused = CONNECTOR_DESCRIPTORS.filter(
        (d) => !isConnectorEnabled(config.connectors, d.key),
      ).length;
      return paused > 0 ? `${paused} paused` : null;
    }
    case "system-prompt": {
      const overridden =
        (ct.system_prompt ?? ct.customer_instructions ?? ct.staff_instructions) !==
        undefined;
      return overridden ? "on" : null;
    }
    case "mailboxes": {
      // SCANNED, not declared. A declared-but-unscanned mailbox is a reply
      // identity only — nothing reads it — and counting those would overstate
      // how much mail the lane actually sees.
      const n = (config.gmail?.mailboxes ?? []).filter((m) => m.scan).length;
      return n > 0 ? `${n} scanned` : null;
    }
    default:
      return null;
  }
}

export function StudioConfig(): JSX.Element {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [config, setConfig] = useState<StudioL2ConfigShape | null>(null);
  const [original, setOriginal] = useState<StudioL2ConfigShape | null>(null);
  const [sectionsMeta, setSectionsMeta] = useState<
    ReadonlyArray<StudioConfigSectionMetadata>
  >([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);
  // to-do #4 (Console layout) — which section owns the main pane, persisted
  // per studio, plus the nav search filter. Sections are grouped in a fixed
  // left rail; one form shows at a time (a settings-app console).
  const [activeSection, setActiveSection] = useState<string | null>(() =>
    loadString(activeSectionKey(slug)),
  );
  const [navSearch, setNavSearch] = useState("");
  // Bumped by the pane-head "Add trainer" modal so the trainers section
  // (which owns its own canonical-table load, not the YAML config state)
  // refetches its roster after a create.
  const [trainersRefreshKey, setTrainersRefreshKey] = useState(0);
  // Phase 66c — code-default prompt text used to seed the System prompt
  // editor textareas when the studio has no override.
  const [promptDefaults, setPromptDefaults] = useState<{
    system_prompt: string;
    customer_instructions: string;
    staff_instructions: string;
  } | null>(null);
  // Phase 48 — section #9 Vendor credentials pending edits.
  // Map of kind → value (non-empty string = upsert; null = delete).
  // Absent key = no pending edit for that kind.
  const [pendingSecrets, setPendingSecrets] = useState<
    Record<string, string | null>
  >({});

  // Phase 67 — the studio config load is cache-backed (key
  // `studio-config:<slug>`). Returning to the editor renders the
  // last-loaded config instantly; the value revalidates when stale or
  // after a save (`invalidateStudioConfig`). Local edit state
  // (`config`/`original`) is seeded from the cache value below so all
  // the existing dirty-tracking / Save semantics are unchanged.
  const cfgKey = `studio-config:${slug ?? "__none__"}`;
  const { data: cfgData, error: cfgError } =
    useCachedResource<StudioConfigResponse>(cfgKey, () => api.getStudioConfig(slug ?? ""), {
      enabled: slug !== undefined,
    });

  useEffect(() => {
    if (slug === undefined) {
      setError("Missing slug in URL");
      setLoading(false);
      return;
    }
    if (cfgError !== null) {
      if (cfgError instanceof ApiError) {
        setError(
          `api_error:${cfgError.status}${cfgError.code ? `:${cfgError.code}` : ""}`,
        );
      } else {
        setError(cfgError.message);
      }
      setLoading(false);
      return;
    }
    if (cfgData !== undefined) {
      // Only seed local edit state ONCE per loaded snapshot — re-seeding
      // on every render would stomp in-progress edits. We key the seed
      // on whether `original` is still null (fresh load / post-Reset).
      const c = cfgData.config as StudioL2ConfigShape;
      setSectionsMeta(cfgData.sections_metadata);
      setConfig((prev) => prev ?? structuredClone(c));
      setOriginal((prev) => prev ?? structuredClone(c));
      // Phase 66c — the code-default prompt text used to seed the System
      // prompt editor. Stable per snapshot, so a plain set is fine (no
      // in-progress edit to stomp — it isn't part of `config`).
      setPromptDefaults(cfgData.prompt_defaults ?? null);
      setError(null);
      setLoading(false);
    }
  }, [slug, cfgData, cfgError]);

  const hasChanges = useMemo(() => {
    if (config === null || original === null) return false;
    // YAML dirty OR secrets dirty triggers Save enable. Phase 48 — both
    // dispatch independently when Save is clicked.
    const yamlDirty =
      JSON.stringify(config) !== JSON.stringify(original);
    const secretsDirty = Object.keys(pendingSecrets).length > 0;
    return yamlDirty || secretsDirty;
  }, [config, original, pendingSecrets]);

  // to-do #4 — the live section ids in default layout order. Slug-scoped
  // sections (staff/vendor) + the config-conditional External integrations
  // section drop out when not applicable, so the drag-order reconciles
  // against whatever is actually rendered.
  const liveSectionIds = useMemo<string[]>(() => {
    if (config === null) return [];
    const ids: string[] = [
      "contact",
      "timezone",
      "branding",
      "pricing",
      "session-types",
      "ai",
      "system-prompt",
      "chat-tools",
      "memory",
      "automations",
    ];
    if (slug !== undefined) ids.push("staff-allowlist");
    ids.push("personal-trainers", "trainer-payroll");
    if (slug !== undefined) ids.push("vendor-credentials");
    // Always live: a studio with no `gmail` block renders the empty state,
    // which is the honest reading ("nothing is scanned") rather than a section
    // that vanishes and leaves an operator with nowhere to declare the first
    // mailbox.
    ids.push("mailboxes");
    ids.push(
      "knowledge-base",
      "marketing-flows",
      "booking-system",
      "connectors",
    );
    if (config.booking_system.kind !== "layer-canonical")
      ids.push("external-integrations");
    return ids;
  }, [config, slug]);

  // Keep `activeSection` valid: once the live sections are known, if none is
  // selected (or the stored one vanished for this studio) default to the first
  // live section so the pane is never blank.
  useEffect(() => {
    if (liveSectionIds.length === 0) return;
    setActiveSection((prev) =>
      prev !== null && liveSectionIds.includes(prev)
        ? prev
        : (liveSectionIds[0] ?? null),
    );
  }, [liveSectionIds]);

  // Persist the active section per studio.
  useEffect(() => {
    if (slug === undefined || activeSection === null) return;
    try {
      localStorage.setItem(activeSectionKey(slug), activeSection);
    } catch {
      /* non-fatal */
    }
  }, [slug, activeSection]);

  // Phase 41 follow-up — Save auto-reloads the receiver (SIGUSR2 in
  // dev; degrades to a "redeploy required" warning in production). The
  // standalone Reload button was removed: there's no scenario at MVP
  // where an operator wants Save + Reload separated. The reload is
  // best-effort — if it fails (e.g. dev pidfile gone), the YAML is
  // still saved to disk + the next process restart picks up the change.
  const handleSave = useCallback(
    async (sectionsTouched: ReadonlyArray<string>) => {
      if (config === null || slug === undefined) return;
      setSaving(true);
      setError(null);
      setStatusMsg(null);
      let saveStatus = "";
      const yamlDirty =
        original !== null &&
        JSON.stringify(config) !== JSON.stringify(original);
      const secretsDirty = Object.keys(pendingSecrets).length > 0;
      try {
        if (yamlDirty) {
          const resp = await api.saveStudioConfig(slug, {
            request_id: uuidv7(),
            patch: config as unknown as Record<string, unknown>,
            sections_touched: sectionsTouched,
          });
          if (resp.ok && resp.response.written) {
            setOriginal(structuredClone(config));
            // Phase 67 — pin the just-saved config into the shared cache
            // so a background revalidation can't briefly flash pre-save
            // YAML, then invalidate the catalog (chat-translator config
            // derived from this YAML) + secrets so they refetch.
            const prevEntry = peekEntry<StudioConfigResponse>(`studio-config:${slug}`);
            setEntry(`studio-config:${slug}`, {
              config: config as unknown as Record<string, unknown>,
              studio_slug: slug,
              sections_metadata:
                prevEntry?.value?.sections_metadata ?? sectionsMeta,
            });
            invalidateStudioConfig(slug);
            saveStatus = `Saved. fields_changed: ${resp.response.fields_changed.join(", ") || "(none)"}`;
          }
        } else {
          saveStatus = "No YAML changes";
        }
      } catch (e) {
        if (e instanceof ApiError) {
          setError(
            `save_failed (HTTP ${e.status}${e.code ? `, code=${e.code}` : ""}): ${e.message}`,
          );
        } else {
          setError(`save_failed: ${(e as Error).message}`);
        }
        setSaving(false);
        return;
      }
      // Phase 48 — write secrets via the dedicated endpoint. Failure of
      // either path does NOT roll back the other (per sub-decision J).
      if (secretsDirty) {
        try {
          const resp = await api.saveStudioSecrets(slug, {
            request_id: uuidv7(),
            secrets: pendingSecrets,
          });
          if (resp.ok && resp.response.updated) {
            // Phase 67 — bust the cached secrets presence so the section
            // refetches the new present/updated_at state.
            invalidate(`studio-secrets:${slug}`);
            saveStatus += ` · Secrets: ${resp.response.kinds_changed.length} changed, ${resp.response.kinds_deleted.length} deleted`;
            setPendingSecrets({});
          }
        } catch (e) {
          // Secrets save failed but YAML save (if any) already landed.
          if (e instanceof ApiError) {
            setError(
              `secrets_save_failed (HTTP ${e.status}${e.code ? `, code=${e.code}` : ""}): ${e.message}`,
            );
          } else {
            setError(`secrets_save_failed: ${(e as Error).message}`);
          }
          setSaving(false);
          return;
        }
      }

      // Save succeeded — auto-reload the receiver. Failures from the
      // reload don't roll back the save; we just surface a friendlier
      // status line.
      try {
        const reloadResp = await api.reloadStudioConfig(slug);
        if (reloadResp.reloaded === true) {
          setStatusMsg(`${saveStatus} · Changes live for the chat agent.`);
        } else if (reloadResp.mode === "production-redeploy-required") {
          setStatusMsg(
            `${saveStatus} · Production: a redeploy is required for the chat agent to pick up changes.`,
          );
        } else {
          setStatusMsg(
            `${saveStatus} · Reload status unclear (code: ${reloadResp.code ?? "unknown"}).`,
          );
        }
      } catch (e) {
        if (
          e instanceof ApiError &&
          e.code === "studio_config_reload_failed"
        ) {
          setStatusMsg(
            `${saveStatus} · Production: a redeploy is required for the chat agent to pick up changes.`,
          );
        } else {
          setStatusMsg(
            `${saveStatus} · Reload failed (chat agent still on old YAML): ${(e as Error).message}`,
          );
        }
      } finally {
        setSaving(false);
      }
    },
    [config, slug, original, pendingSecrets],
  );

  const handleReset = useCallback(() => {
    if (original === null) return;
    setConfig(structuredClone(original));
    // Phase 48 — also clear pending secret edits on Reset.
    setPendingSecrets({});
    setStatusMsg("Reverted to last-saved state");
  }, [original]);

  if (loading) {
    return (
      <div className="page-studio-config">
        <h1>Edit studio: {slug}</h1>
        <p>Loading…</p>
      </div>
    );
  }
  if (error !== null && config === null) {
    return (
      <div className="page-studio-config">
        <h1>Edit studio: {slug}</h1>
        <div className="tag tag-danger">Error: {error}</div>
        <p>
          <button type="button" onClick={() => navigate("/vision")}>
            Back to dashboard
          </button>
        </p>
      </div>
    );
  }
  if (config === null) return <div>No config loaded.</div>;

  // to-do #4 — the rendered node for each live section id. Phase 41
  // sub-decision L1 (External integrations hidden for layer-canonical
  // studios) + the slug-scoped sections are already reflected in
  // `liveSectionIds`, so this map only needs an entry per live id.
  const nodeById: Record<string, JSX.Element> = {
    contact: <ContactInformationSection config={config} setConfig={setConfig} />,
    timezone: <StudioTimezoneSection />,
    branding: <BrandingSection config={config} setConfig={setConfig} />,
    pricing: <PricingSection config={config} setConfig={setConfig} />,
    "session-types": (
      <SessionTypesSection config={config} setConfig={setConfig} />
    ),
    ai: <AiVoiceSection config={config} setConfig={setConfig} />,
    "system-prompt": (
      <SystemPromptOverrideSection
        config={config}
        setConfig={setConfig}
        promptDefaults={promptDefaults}
      />
    ),
    "chat-tools": (
      <ChatToolCatalogSection config={config} setConfig={setConfig} />
    ),
    memory: <MemorySection config={config} setConfig={setConfig} />,
    automations: <ScheduledJobsSection />,
    connectors: <ConnectorsSection config={config} setConfig={setConfig} />,
    "personal-trainers": (
      <PersonalTrainersSection
        slug={slug}
        config={config}
        setConfig={setConfig}
        refreshKey={trainersRefreshKey}
      />
    ),
    "trainer-payroll": (
      <TrainerPayrollSection config={config} setConfig={setConfig} />
    ),
    mailboxes: <GmailMailboxesSection config={config} setConfig={setConfig} />,
    "knowledge-base": (
      <KnowledgeBaseSection config={config} setConfig={setConfig} />
    ),
    "marketing-flows": (
      <MarketingFlowsSection config={config} setConfig={setConfig} />
    ),
    "booking-system": (
      <BookingSystemSection config={config} setConfig={setConfig} />
    ),
    "external-integrations": (
      <ExternalIntegrationsSection config={config} setConfig={setConfig} />
    ),
  };
  if (slug !== undefined) {
    nodeById["staff-allowlist"] = (
      <StaffAllowlistSection
        slug={slug}
        onOpenTrainers={() => setActiveSection("personal-trainers")}
      />
    );
    nodeById["vendor-credentials"] = (
      <VendorCredentialsSection
        slug={slug}
        pendingSecrets={pendingSecrets}
        setPendingSecrets={setPendingSecrets}
      />
    );
  }

  // Console layout — which section owns the main pane on THIS render (fall
  // back to the first live section if the reconcile effect hasn't run yet).
  const liveSet = new Set(liveSectionIds);
  const paneId =
    activeSection !== null && liveSet.has(activeSection)
      ? activeSection
      : (liveSectionIds[0] ?? null);
  const paneNode = paneId !== null ? nodeById[paneId] : undefined;
  const search = navSearch.trim().toLowerCase();
  const matchesSearch = (id: string): boolean => {
    if (search === "") return true;
    const label = SECTION_NAV_LABEL[id] ?? SECTION_TITLES[id] ?? id;
    const full = SECTION_TITLES[id] ?? "";
    return (
      label.toLowerCase().includes(search) ||
      full.toLowerCase().includes(search) ||
      id.includes(search)
    );
  };

  return (
    <div className="page-studio-config config-console-page">
      {/* `page-title` so it carries the same weight as Data / Tasks / Flows —
          a bare `h1` here rendered at a third of their size. The studio slug
          dropped with it: there is one studio, its name is in the top bar, and
          the slug made the title a compound thing instead of a page name. The
          full-replace warning moved to the Save control, where the person
          about to overwrite the YAML is actually looking. */}
      <h1 className="page-title">Studio Config</h1>
      {error !== null && <div className="tag tag-danger">Error: {error}</div>}
      {statusMsg !== null && <div className="tag tag-success">{statusMsg}</div>}

      <div className="config-console">
        <nav className="config-console-nav" aria-label="Config sections">
          <input
            type="search"
            className="config-console-search"
            placeholder="Search settings…"
            value={navSearch}
            onChange={(e) => setNavSearch(e.target.value)}
          />
          {SECTION_GROUPS.map((grp) => {
            const items = grp.ids.filter(
              (id) => liveSet.has(id) && matchesSearch(id),
            );
            if (items.length === 0) return null;
            return (
              <div className="config-console-group" key={grp.group}>
                <div className="config-console-group-label">{grp.group}</div>
                {items.map((id) => {
                  const label = SECTION_NAV_LABEL[id] ?? SECTION_TITLES[id] ?? id;
                  const badge = navBadge(id, config);
                  return (
                    <button
                      type="button"
                      key={id}
                      className={`config-console-item${paneId === id ? " on" : ""}`}
                      onClick={() => setActiveSection(id)}
                      aria-current={paneId === id ? "page" : undefined}
                    >
                      <span className="config-console-item-label">{label}</span>
                      {badge !== null && (
                        <span className="config-console-badge">{badge}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </nav>

        <div className="config-console-pane">
          <div className="config-console-pane-head">
            <div className="config-console-pane-titles">
              <h2 className="config-console-pane-title">
                {paneId !== null ? (SECTION_TITLES[paneId] ?? paneId) : "Settings"}
              </h2>
              {paneId !== null && SECTION_SUBTITLE[paneId] !== undefined && (
                <p className="config-console-pane-sub">
                  {SECTION_SUBTITLE[paneId]}
                </p>
              )}
            </div>
            <div className="config-console-actions">
              {hasChanges && (
                <span className="config-console-dirty">Unsaved changes</span>
              )}
              {/* Trainers pane only: creating a trainer is a canonical-table
                  write (dispatch, immediate), not part of the YAML save. */}
              {paneId === "personal-trainers" && (
                <AddTrainerButton
                  onCreated={() => setTrainersRefreshKey((k) => k + 1)}
                />
              )}
              {/* Full-replace is the one thing about this page that can
                  surprise you, so it rides the control that does it rather
                  than a subtitle at the top of a long scroll. */}
              <button
                type="button"
                className="config-console-save"
                disabled={saving || !hasChanges}
                title={`Writes the WHOLE verticals/ems/studios/${slug}.yaml — full-replace, not a patch.`}
                onClick={() => handleSave(sectionsMeta.map((s) => s.id))}
              >
                {saving ? "Saving…" : "Save all changes"}
              </button>
              <button
                type="button"
                className="config-console-reset"
                disabled={!hasChanges}
                onClick={handleReset}
              >
                Reset
              </button>
            </div>
          </div>
          <div className="config-console-body">
            {paneNode ?? (
              <p className="subtle">Select a section on the left.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
